import Phaser from 'phaser'
import { ApiConfig } from '@/config/ApiConfig'
import { AssetKeys } from '@/config/AssetKeys'
import {
  Grid,
  GridSize,
  Layout,
  BET_LEVELS,
  DEFAULT_BET,
  setSpinPreset,
} from '@/config/SlotConfig'
import { ReelGrid } from '@/slot/ReelGrid'
import { SpinService } from '@/slot/SpinService'
import { GameContext } from '@/config/GameContext'
import { AuthService } from '@/net/AuthService'
import { HttpClient } from '@/net/HttpClient'
import {
  CANDY_LAZY_FS_ASSETS,
  CANDY_LAZY_FS_AUDIO,
  CANDY_LAZY_WIN_ASSETS,
  CANDY_LAZY_WIN_AUDIO,
} from '@/game/assets/assetManifest'
import type { CascadeStep, SpinResponse, SymbolKey } from '@sss/shared'

/**
 * SpinState — 显式状态机，所有 spin 入口都 guard 在这上面
 *
 *  idle ──click Spin──▶ spinning
 *  spinning ──reels stopped──▶ evaluating
 *  evaluating ──no win──▶ idle
 *  evaluating ──has win──▶ showingWin ──effects done──▶ idle
 */
type SpinState = 'idle' | 'spinning' | 'evaluating' | 'showingWin'

/**
 * GameScene — Neon Candy Fortune 主屏（Slotomania 竖版风格）
 *
 * 视觉布局：
 *   y=0-100    Top bar (Menu | Logo | Paytable | Settings)
 *   y=110-440  NC-017 Jackpot Ladder (GRAND/MAJOR/MINOR/MINI)
 *   y=460-1240 Reel zone (5×3 cell=170, neon glow border)
 *   y=1260-1380 HUD (Balance | Total Bet | Win)
 *   y=1400-1732 NC-018 Control bar (Bet− | Bet+ | Spin)
 *   y=1820     Utility row (Auto / Turbo / MaxBet / BuyBonus)
 */
export class GameScene extends Phaser.Scene {
  private reelGrid!: ReelGrid

  // 经济状态——0 是占位，create() 里 hydrateRealBalance() async 拉 /auth/me 写真值。
  // 之前默认 88_888_888 是给 dev 离线预览的：上线后用户进游戏先看到 8888 万再跳真值，
  // 像炫耀型假象，并触发 "余额暴跌" 错觉。改 0，hydrate 之前 HUD 显示 "—"。
  private balance = 0
  private balanceHydrated = false
  private bet = DEFAULT_BET
  private betIndex = BET_LEVELS.indexOf(DEFAULT_BET as never)

  // 状态机
  private spinState: SpinState = 'idle'
  // Auto/Turbo 按钮已移除，留 turboOn 字段供后续 Lobby toggle 用
  private turboOn = false
  // Auto-spin（由 parent player-web 通过 postMessage 控制，每局结束后自动接下一局）
  private autoSpinEnabled = false
  private autoSpinDelayMs = 1800

  // ─── Free Spins 状态（client-side mirror of backend fsSession） ──
  private fsRemaining = 0     // 还剩多少 FS spin（>0 → FS 模式）
  private fsTotalWin = 0      // FS round 累计赢额
  private fsAccumulator = 0   // Sweet Bonanza-style 跨 spin 累积乘子
  private fsCounterText: Phaser.GameObjects.Text | null = null  // FS 期间显示"FS 3/10 ×8"

  // Jackpot rolling 值（GRAND / MAJOR / MINOR — Premium Lounge horizontal strip）
  private jackpotEntries: {
    text: Phaser.GameObjects.Text
    value: number
    /** 兼容旧 4-tier 字段，新设计不再使用（横条文本绝对定位） */
    boxDispW: number
    boxDispH: number
    prefix: string
  }[] = []
  private jackpotTickerEvent: Phaser.Time.TimerEvent | null = null

  // UI 引用
  private balanceText!: Phaser.GameObjects.Text
  private betText!: Phaser.GameObjects.Text
  private winText!: Phaser.GameObjects.Text
  private spinBtn!: Phaser.GameObjects.Image
  private spinBtnBaseScale = 1
  private spinTween: Phaser.Tweens.Tween | null = null
  private spinIdleTween: Phaser.Tweens.Tween | null = null
  private spinIdlePulseTween: Phaser.Tweens.Tween | null = null
  private bgm: Phaser.Sound.BaseSound | null = null
  private backgroundObjects: Phaser.GameObjects.GameObject[] = []
  private activeModal: Phaser.GameObjects.Container | null = null
  private candyFeverLive = false
  private candyFeverVisuals: Phaser.GameObjects.Container | null = null
  private thermalQuiet = false
  private lastInteractionAt = Date.now()
  private thermalCheckEvent: Phaser.Time.TimerEvent | null = null
  private lazyAssetsWarmupStarted = false

  constructor() {
    super({ key: 'GameScene' })
  }

  create(): void {
    // 入 scene 第一件事：硬清音频残留
    this.sound.stopAll()
    this.centerSafeAreaCamera()
    // 通知 parent player-web：场景资源已就绪（assets 加载完，grid 即将渲染），
    // 让 PhaserIframe 的 "Walking to your seat" loader 立刻消失。比依赖
    // sss-iframe-ready 准 5-7s——后者只代表 AuthService 启动了。
    try { window.parent?.postMessage({ type: 'sss-scene-ready' }, '*') } catch { /* ignore */ }

    // Sakura uses the same global 10K minimum spend as the backend whitelist.
    if (GameContext.gameId() === 'sakura') {
      const SAKURA_MIN_BET = 10_000
      const minIdx = BET_LEVELS.findIndex((b) => b >= SAKURA_MIN_BET)
      if (minIdx >= 0 && this.betIndex < minIdx) {
        this.betIndex = minIdx
        this.bet = BET_LEVELS[minIdx]!
      }
    }

    this.drawBackground()
    const onResize = () => {
      this.centerSafeAreaCamera()
      this.drawBackground()
    }
    this.scale.on(Phaser.Scale.Events.RESIZE, onResize)
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.scale.off(Phaser.Scale.Events.RESIZE, onResize))

    // 2 个游戏分开渲染策略（每个 game 独立 themeAssets + 独立资源）：
    //  - candy   : 完整 cluster_tumble 玩法（自己的 SymbolToAsset + 完整 UI）
    //  - sakura  : 5×3 walking_wilds（IMG-018/020/022/061-...）themeAssets 完备
    this.drawTopBar()
    this.drawJackpotHeader()
    this.drawReelZone()
    this.drawHud()
    this.drawControlBar()
    // 底部 4 按钮（Auto/Turbo/MaxBet/BuyBonus）已移除 — 后续在 Lobby / Settings 内提供
    this.armBgm()
    this.armAnimationDebug()
    this.armThermalControls()

    // 提前发起登录并拉真实余额，让首 spin 无 auth 等待；iframe URL 已带 parent token。
    // Sakura 也必须走这里，否则进房 HUD 会一直显示 "—"，且余额不足 guard 会让 SPIN 不可用。
    AuthService.ensure()
      .then(() => this.hydrateRealBalance())
      .catch((err) => console.warn('[auth] ensure failed', err))

    this.scheduleLazyAssetWarmup()

    // Scene 关闭时清理音频
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanupAudio())
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.cleanupAudio())

    // Watch 模式：禁 SPIN + 顶端 WATCHING 水印
    try {
      const params = new URLSearchParams(window.location.search)
      if (params.get('mode') === 'watch') {
        this.applyWatchMode()
      }
    } catch { /* ignore */ }

    // 接收 parent player-web postMessage：autospin / replay-spin（spectator 视角）
    const onParentMsg = (e: MessageEvent) => {
      const data = e.data
      if (!data || typeof data !== 'object') return
      if (data.type === 'sss-autospin') {
        this.autoSpinEnabled = !!data.enabled
        if (typeof data.delay_ms === 'number') {
          this.autoSpinDelayMs = Math.max(800, Math.min(8000, data.delay_ms))
        }
        if (this.autoSpinEnabled && this.spinState === 'idle' && this.balance >= this.bet && this.fsRemaining === 0) {
          this.time.delayedCall(this.autoSpinDelayMs, () => this.maybeAutoSpin())
        }
      }
      if (data.type === 'sss-replay-spin') {
        const winAmount = typeof data.win_amount === 'number' ? data.win_amount : 0
        void this.replayVisualSpin(winAmount)
      }
      // Live 直播间真实回放：直接拿后端 cluster_tumble 的真实 result (KOL 那次实际盘面 + cascades)
      // 走原生 runCascades 系统播。彻底消灭 generateRandomGrid/generateWinningGrid 那条假视觉路径，
      // 让观众看到的 cluster 和宣布的 win 数字完全对得上。老的 sss-replay-spin 保留作为
      // 滚动部署兜底——backend 老版本还在发 win_amount-only payload 时不会断。
      if (data.type === 'sss-replay-spin-full') {
        const grid = Array.isArray(data.initial_grid) ? data.initial_grid : null
        const cascades = Array.isArray(data.cascades) ? data.cascades : []
        const totalWin = typeof data.total_win === 'number' ? data.total_win : 0
        if (grid) void this.replayRealSpin(grid, cascades, totalWin)
      }
      if (data.type === 'sss-room-fever-state') {
        this.setCandyFeverLive(
          data.active === true,
          typeof data.ends_at === 'string' ? data.ends_at : null,
        )
        // Heat anticipation pulse — start the looping cue at Heat ≥ 85%, stop
        // when Heat drops back below 80% (5pct hysteresis to avoid flapping at
        // the boundary). Skipped during Fever (the fever loop replaces it).
        const heat = typeof data.heat_pct === 'number' ? data.heat_pct : null
        if (GameContext.gameId() === 'candy' && heat !== null && data.active !== true) {
          if (heat >= 85 && !this.anticipationSound) this.startAnticipationPulse()
          else if (heat < 80 && this.anticipationSound) this.stopAnticipationPulse()
        }
      }
      // 玩家在房间里点了别人的头像 → iframe 进观摩模式，把 SPIN disable + WATCHING 水印 +
      // BALANCE/TOTAL BET pill 切到 ta 的数据
      if (data.type === 'sss-set-watch-mode') {
        // 第一次进 watch 才记自己的真实余额，避免连点几次后丢失原值
        if (this.savedSelfBalance === null) this.savedSelfBalance = this.balance
        if (this.savedSelfBet === null) this.savedSelfBet = this.bet
        this.applyWatchMode(typeof data.display_name === 'string' ? data.display_name : undefined)
        // 观摩模式 SPIN 被 disable → 玩家不会在 iframe 内 pointerdown → BGM / SFX 一直处于 audio
        // context suspended 状态。这里主动尝试 resume + 启动 BGM。Browser 大多需要在 user gesture 内
        // 调用，所以这里能不能成功取决于父页面的 click 是否被算作 user activation（Chrome 通常是）。
        this.tryUnlockAudio()
        if (typeof data.balance === 'number') {
          this.balance = data.balance
          if (this.balanceText) {
            this.balanceText.setText(fmt(data.balance))
            fitTextToWidth(this.balanceText, 265)
          }
        }
        if (typeof data.bet_amount === 'number' && data.bet_amount > 0) {
          this.bet = data.bet_amount
          if (this.betText) {
            this.betText.setText(fmt(data.bet_amount))
            fitTextToWidth(this.betText, 220)
          }
        }
      }
      // 玩家点回自己头像 / 点了 × → 退出观摩模式：去掉 watermark，恢复自己 SPIN 可点 + 余额/下注。
      if (data.type === 'sss-clear-watch-mode') {
        this.clearWatchMode()
        if (this.savedSelfBalance !== null) {
          this.balance = this.savedSelfBalance
          if (this.balanceText) {
            this.balanceText.setText(fmt(this.balance))
            fitTextToWidth(this.balanceText, 265)
          }
          this.savedSelfBalance = null
        }
        if (this.savedSelfBet !== null) {
          this.bet = this.savedSelfBet
          if (this.betText) {
            this.betText.setText(fmt(this.bet))
            fitTextToWidth(this.betText, 220)
          }
          this.savedSelfBet = null
        }
      }
    }
    window.addEventListener('message', onParentMsg)
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => window.removeEventListener('message', onParentMsg))
  }

  private armThermalControls(): void {
    const wake = () => {
      this.lastInteractionAt = Date.now()
      if (this.thermalQuiet) this.exitThermalQuiet()
    }
    window.addEventListener('pointerdown', wake, { passive: true })
    window.addEventListener('touchstart', wake, { passive: true })
    window.addEventListener('keydown', wake)
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener('pointerdown', wake)
      window.removeEventListener('touchstart', wake)
      window.removeEventListener('keydown', wake)
      this.thermalCheckEvent?.remove(false)
      this.thermalCheckEvent = null
    })
    this.thermalCheckEvent = this.time.addEvent({
      delay: 5000,
      loop: true,
      callback: () => {
        if (!isMobileThermalDevice()) return
        if (this.thermalQuiet) return
        if (Date.now() - this.lastInteractionAt < 12_000) return
        if (this.spinState !== 'idle' || this.autoSpinEnabled || this.fsRemaining > 0 || this.candyFeverLive || this.activeModal) return
        this.enterThermalQuiet()
      },
    })
  }

  private enterThermalQuiet(): void {
    this.thermalQuiet = true
    this.stopSpinIdleMotion()
    this.stopAnticipationPulse()
    this.pauseBgm()
  }

  private exitThermalQuiet(): void {
    this.thermalQuiet = false
    if (this.spinState === 'idle') this.startSpinIdleMotion()
    this.resumeBgm()
  }

  private scheduleLazyAssetWarmup(): void {
    if (this.lazyAssetsWarmupStarted || GameContext.gameId() !== 'candy') return
    this.lazyAssetsWarmupStarted = true
    const mobile = isMobileThermalDevice()
    // Mobile Safari gets hot when it decodes multiple 1080×1919 overlays right
    // after boot. Warm these in small idle chunks instead of all at 800ms.
    this.time.delayedCall(mobile ? 6_000 : 800, () => {
      if (document.hidden) return
      void this.loadLazyAssets('win', CANDY_LAZY_WIN_ASSETS, CANDY_LAZY_WIN_AUDIO)
    })
    this.time.delayedCall(mobile ? 12_000 : 1_500, () => {
      if (document.hidden) return
      void this.loadLazyAssets('fs', CANDY_LAZY_FS_ASSETS, CANDY_LAZY_FS_AUDIO)
    })
  }

  private async replayVisualSpin(winAmount: number = 0): Promise<void> {
    if (document.hidden) return
    this.lastInteractionAt = Date.now()
    if (this.thermalQuiet) this.exitThermalQuiet()
    if (this.spinState !== 'idle') return
    this.spinState = 'spinning'
    this.startSpinRotation()
    this.playSfx(this.sfx('spin_tap'), 0.45)
    this.time.delayedCall(120, () => this.playSfx(this.sfx('reels_start'), 0.4))
    this.reelGrid.spin()
    await delay(540)
    const stopGap = 150
    for (let c = 0; c < Grid.COLS; c++) {
      this.time.delayedCall(c * stopGap, () => this.playSfx(this.sfx('reel_stop'), 0.4))
    }
    // 若 win > 0，生成带 cluster 的 grid（5+ 同 symbol），让玩家直观看到「中奖」
    let clusterCells: [number, number][] = []
    let clusterSymbol: SymbolKey = 'CHERRY'
    let grid: SymbolKey[][]
    if (winAmount > 0) {
      const w = generateWinningGrid()
      grid = w.grid
      clusterCells = w.clusterCells
      clusterSymbol = w.symbol
    } else {
      grid = generateRandomGrid()
    }
    await this.reelGrid.stop(grid)
    this.stopSpinRotation()

    // win > 0：跑一次假 cascade（高亮 + pop + 粒子 + vanish + drop fill），让 spectator 看到完整中奖过程
    if (winAmount > 0 && clusterCells.length > 0) {
      this.playSfx(winAmount >= 1000 ? this.sfx('big_win_count') : this.sfx('small_win'), 0.55)
      const fakeStep: CascadeStep = {
        round: 0,
        round_win: winAmount,
        grid_after: generateRandomGrid(),
        clusters: [{
          payline_id: 'replay_cluster',
          symbol: clusterSymbol,
          count: clusterCells.length,
          win_amount: winAmount,
          positions: clusterCells,
        }],
      }
      await this.runCascades([fakeStep], 0)
      // 1.2s 后归零，准备下一次 replay
      this.time.delayedCall(1200, () => {
        if (this.winText) {
          this.winText.setText('0')
          fitTextToWidth(this.winText, 130)
        }
      })
    }
    this.spinState = 'idle'
  }

  /**
   * 真实 replay — 后端推真实 cluster_tumble result（KOL 那次实际开局盘 + cascades），
   * 用现成的 runCascades 系统播。观众看到的 grid / cluster / 消除步骤都是 KOL 真实数据，
   * 跟 4 人房 / solo 玩家自己 spin 看到的判定逻辑完全一致。
   */
  private async replayRealSpin(initialGrid: SymbolKey[][], cascades: CascadeStep[], totalWin: number): Promise<void> {
    if (document.hidden) return
    this.lastInteractionAt = Date.now()
    if (this.thermalQuiet) this.exitThermalQuiet()
    if (this.spinState !== 'idle') return
    this.spinState = 'spinning'
    this.startSpinRotation()
    this.playSfx(this.sfx('spin_tap'), 0.45)
    this.time.delayedCall(120, () => this.playSfx(this.sfx('reels_start'), 0.4))
    this.reelGrid.spin()
    await delay(540)
    const stopGap = 150
    for (let c = 0; c < Grid.COLS; c++) {
      this.time.delayedCall(c * stopGap, () => this.playSfx(this.sfx('reel_stop'), 0.4))
    }
    await this.reelGrid.stop(initialGrid)
    this.stopSpinRotation()

    if (cascades.length > 0 && totalWin > 0) {
      this.playSfx(totalWin >= 1000 ? this.sfx('big_win_count') : this.sfx('small_win'), 0.55)
      await this.runCascades(cascades, 0)
      this.time.delayedCall(1200, () => {
        if (this.winText) {
          this.winText.setText('0')
          fitTextToWidth(this.winText, 130)
        }
      })
    }
    this.spinState = 'idle'
  }

  private watchModeText: Phaser.GameObjects.Text | null = null
  /** 进 watch 模式前快照玩家自己的余额/下注，clearWatchMode 时恢复 */
  private savedSelfBalance: number | null = null
  private savedSelfBet: number | null = null

  private applyWatchMode(displayName?: string): void {
    const label = displayName ? `WATCHING ${displayName.toUpperCase()}` : 'WATCHING'
    if (this.watchModeText) {
      this.watchModeText.setText(label)
      return
    }
    // 等 spinBtn 被 drawControlBar 创建后再 disable + 半透明
    this.time.delayedCall(50, () => {
      if (this.spinBtn) {
        this.spinBtn.disableInteractive()
        this.spinBtn.setAlpha(0.35)
      }
      // 顶端 WATCHING 状态（绝对定位 game canvas 上方居中）
      const w = this.scale.width
      const text = this.add.text(w / 2, 22, label, {
        fontFamily: 'Plus Jakarta Sans, sans-serif',
        fontSize: '20px',
        color: '#fbb567',  // rose-gold
        fontStyle: 'bold',
        stroke: '#0e0824',
        strokeThickness: 4,
        backgroundColor: 'rgba(14, 8, 36, 0.62)',
        padding: { x: 18, y: 8 },
      })
      text.setOrigin(0.5, 0)
      text.setLetterSpacing(3)
      text.setDepth(10000)
      text.setScrollFactor(0)
      this.watchModeText = text
    })
  }

  /** 退出 watch 模式：恢复 SPIN 按钮可点，去掉 WATCHING 水印。 */
  private clearWatchMode(): void {
    if (this.watchModeText) {
      this.watchModeText.destroy()
      this.watchModeText = null
    }
    if (this.spinBtn) {
      this.spinBtn.setInteractive()
      this.spinBtn.setAlpha(1)
    }
    // 余额/win/bet 不在这里改：玩家下次 SPIN 触发 wallet 接口时自然刷新。
  }

  /** 写 fsRemaining 时统一走这里，自动 broadcast 给 parent player-web，
   *  让 MatchCompleteGate 知道是否要拦截 LOBBY 退出（FS 未完不让走）。 */
  private setFsRemaining(n: number): void {
    this.fsRemaining = n
    try {
      window.parent?.postMessage({ type: 'sss-fs-state', remaining: n }, '*')
    } catch { /* ignore */ }
  }

  private setCandyFeverLive(active: boolean, _endsAt: string | null = null): void {
    if (GameContext.gameId() !== 'candy') return
    const wasActive = this.candyFeverLive
    this.candyFeverLive = active
    if (active) {
      this.showCandyFeverVisuals()
      // First-frame Fever ignite SFX + BGM swap to fever loop. Only fire on
      // the rising edge (was-not-active) so resyncs don't replay the cue.
      if (!wasActive) {
        this.playSfx(this.sfx('fever_ignite'), 0.78)
        this.swapBgmToFever()
        // Anticipation pulse, if it was looping during pre-fever Heat 85+,
        // gets stopped at fever start (the fever loop replaces it).
        this.stopAnticipationPulse()
      }
    } else {
      this.hideCandyFeverVisuals()
      if (wasActive) {
        // Fever ended — celebration cue + restore main BGM.
        this.playSfx(this.sfx('fever_settlement'), 0.7)
        this.swapBgmToMain()
      }
    }
  }

  // ─── Fever audio swap helpers ───────────────────────────────────
  // Each swap stop+destroy the previous bgm sound so the SoundManager doesn't
  // accumulate orphan sounds across many fever cycles (each Fever ignite +
  // settlement = 2 swaps; over 20min on a busy table that's 30+ leaked sound
  // objects holding decoded audio buffers in memory).
  private swapBgmToFever(): void {
    if (!this.bgm) return
    if (!this.cache.audio.exists(AssetKeys.audio.bgm_lucky_fever_loop)) return
    try { this.bgm.stop() } catch { /* ignore */ }
    try { this.bgm.destroy() } catch { /* ignore */ }
    this.bgm = this.sound.add(AssetKeys.audio.bgm_lucky_fever_loop, { loop: true, volume: 0.36 })
    try { this.bgm.play() } catch { /* ignore */ }
  }

  private swapBgmToMain(): void {
    if (!this.bgm) return
    const cfg = GameContext.config()
    const bgmKey = this.cache.audio.exists(cfg.bgmMainKey) ? cfg.bgmMainKey : AssetKeys.audio.bgm_main
    if (!this.cache.audio.exists(bgmKey)) return
    try { this.bgm.stop() } catch { /* ignore */ }
    try { this.bgm.destroy() } catch { /* ignore */ }
    this.bgm = this.sound.add(bgmKey, { loop: true, volume: 0.32 })
    try { this.bgm.play() } catch { /* ignore */ }
  }

  // ─── Anticipation pulse (Heat 85-99% pre-fever) ─────────────────
  private anticipationSound: Phaser.Sound.BaseSound | null = null
  private startAnticipationPulse(): void {
    if (this.thermalQuiet) return
    if (this.anticipationSound) return
    if (!this.cache.audio.exists(AssetKeys.audio.sfx_fever_anticipation_pulse)) return
    this.anticipationSound = this.sound.add(AssetKeys.audio.sfx_fever_anticipation_pulse, { loop: true, volume: isMobileThermalDevice() ? 0.16 : 0.28 })
    try { this.anticipationSound.play() } catch { /* ignore */ }
  }
  private stopAnticipationPulse(): void {
    if (!this.anticipationSound) return
    try { this.anticipationSound.stop() } catch { /* ignore */ }
    try { this.anticipationSound.destroy() } catch { /* ignore */ }
    this.anticipationSound = null
  }

  // ─── Win-tier stinger (big / mega / super / jackpot) ────────────
  /** Plays the per-tier stinger BGM once at win popup entry. Each tier has its
   *  own audio so super/jackpot don't sound identical to big/mega. The
   *  count-loop SFX (sfx_big_win_count) is layered on top inside the popup
   *  body — kept separate so this stinger can be a 3-4s entrance hit. */
  private playWinTierStinger(level: 'big' | 'mega' | 'super' | 'jackpot'): void {
    const key =
      level === 'jackpot' ? AssetKeys.audio.bgm_jackpot_stinger
      : level === 'super' ? AssetKeys.audio.bgm_super_win_stinger
      : level === 'mega'  ? AssetKeys.audio.bgm_mega_win_stinger
      :                     AssetKeys.audio.bgm_big_win_stinger
    if (!this.cache.audio.exists(key)) return
    try {
      const stinger = this.sound.add(key, { volume: 0.62 })
      stinger.play()
    } catch { /* ignore */ }
  }

  /** Holds inner tween targets so hideCandyFeverVisuals can kill them all.
   *  Without this, the repeat:-1 yoyo tweens on label/rim/frame survive past
   *  c.destroy() and keep ticking against destroyed objects, leaking CPU. */
  private candyFeverInnerTargets: Phaser.GameObjects.GameObject[] = []
  private showCandyFeverVisuals(): void {
    if (this.candyFeverVisuals) return
    this.candyFeverInnerTargets = []
    const c = this.add.container(0, 0).setDepth(16).setAlpha(0)

    // 全屏金色边光 — 暗示整桌进入 Fever 状态，玩家从眼角余光就能感到
    const rim = this.add.graphics()
    rim.lineStyle(28, 0xffcf6b, 0.42)
    rim.strokeRoundedRect(8, 8, Layout.CANVAS_W - 16, Layout.CANVAS_H - 16, 18)
    rim.setBlendMode(Phaser.BlendModes.ADD)
    c.add(rim)

    const frame = this.add.graphics()
    frame.lineStyle(16, 0xffcf6b, 0.86)
    frame.strokeRoundedRect(Grid.ORIGIN_X - 34, Grid.ORIGIN_Y - 34, GridSize.WIDTH + 68, GridSize.HEIGHT + 68, 34)
    frame.lineStyle(6, 0xff5bbd, 0.7)
    frame.strokeRoundedRect(Grid.ORIGIN_X - 22, Grid.ORIGIN_Y - 22, GridSize.WIDTH + 44, GridSize.HEIGHT + 44, 26)
    frame.setBlendMode(Phaser.BlendModes.ADD)
    c.add(frame)

    const badgeX = Layout.CANVAS_W / 2
    const badgeY = Math.max(356, Grid.ORIGIN_Y - 78)
    const badge = this.add.graphics()
    badge.fillStyle(0x2a0635, 0.88)
    badge.fillRoundedRect(badgeX - 180, badgeY - 32, 360, 64, 32)
    badge.lineStyle(4, 0xffcf6b, 0.96)
    badge.strokeRoundedRect(badgeX - 180, badgeY - 32, 360, 64, 32)
    c.add(badge)

    const label = this.add.text(badgeX, badgeY, '★ LUCKY FEVER × 2 ★', {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: '34px',
      color: '#fff7c8',
      stroke: '#8a144f',
      strokeThickness: 6,
      shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 6, fill: true },
    }).setOrigin(0.5)
    c.add(label)

    this.candyFeverVisuals = c
    // Track inner targets so hide can kill the infinite tweens — leaving them
    // attached to destroyed objects after fever ends was a major iPhone heat
    // source over 10+ fever cycles per session.
    this.candyFeverInnerTargets = [label, rim, frame]
    this.tweens.add({ targets: c, alpha: 1, duration: 260, ease: 'Cubic.easeOut' })
    // 入场冲击：badge scale 1.4 → 1，帮助玩家"啊 Fever 来了"瞬间被抓住眼睛
    label.setScale(1.4)
    this.tweens.add({
      targets: label,
      scale: 1,
      duration: 380,
      ease: 'Back.easeOut',
      onComplete: () => {
        // Only spawn the breathing tween if the fever overlay is still around.
        // Hide-during-entrance race would otherwise spawn an orphan tween.
        if (!this.candyFeverVisuals) return
        this.tweens.add({
          targets: label,
          scale: 1.06,
          duration: 820,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.inOut',
        })
      },
    })
    // 全屏 rim 呼吸更剧烈
    this.tweens.add({
      targets: rim,
      alpha: 0.18,
      duration: 1100,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    })
    this.tweens.add({
      targets: frame,
      alpha: 0.46,
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    })
    // 单次相机闪光 + 轻震，做"开场"宣告。强度从 0.004 降到 0.002——之前那一震
    // 配合后续可能立即来的 Tumble cascade shake 会让玩家头晕。flash 是最大宣告。
    this.cameras.main.flash(220, 255, 215, 140)
    this.cameras.main.shake(140, 0.002)
  }

  private hideCandyFeverVisuals(): void {
    const c = this.candyFeverVisuals
    if (!c) return
    this.candyFeverVisuals = null
    // Kill BOTH the container's tween AND the infinite tweens we spawned on
    // its inner children (label/rim/frame). killTweensOf(c) does NOT cascade
    // into children — that was the leak.
    this.tweens.killTweensOf(c)
    for (const target of this.candyFeverInnerTargets) {
      try { this.tweens.killTweensOf(target) } catch { /* ignore */ }
    }
    this.candyFeverInnerTargets = []
    this.tweens.add({
      targets: c,
      alpha: 0,
      duration: 220,
      ease: 'Sine.easeIn',
      onComplete: () => c.destroy(true),
    })
  }

  private showCandyFeverWinToast(amount: number): void {
    if (GameContext.gameId() !== 'candy' || !this.candyFeverLive || amount <= 0) return
    const x = 842
    const y = 1328
    const c = this.add.container(x, y + 14).setDepth(620).setAlpha(0)
    const bg = this.add.graphics()
    bg.fillStyle(0xffcf6b, 0.92)
    bg.fillRoundedRect(-142, -28, 284, 56, 28)
    bg.lineStyle(3, 0xffffff, 0.65)
    bg.strokeRoundedRect(-142, -28, 284, 56, 28)
    c.add(bg)
    const text = this.add.text(0, 0, `FEVER WIN +${fmt(amount)}`, {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: '22px',
      color: '#3d062a',
      stroke: '#fff7c8',
      strokeThickness: 3,
    }).setOrigin(0.5)
    c.add(text)
    this.tweens.add({ targets: c, y, alpha: 1, duration: 160, ease: 'Cubic.easeOut' })
    this.tweens.add({
      targets: c,
      y: y - 16,
      alpha: 0,
      delay: 900,
      duration: 260,
      ease: 'Sine.easeIn',
      onComplete: () => c.destroy(true),
    })
  }

  private showCandyTumbleBadge(step: number, totalSteps: number, amount: number): void {
    if (GameContext.gameId() !== 'candy' || amount <= 0) return
    const x = Layout.CANVAS_W / 2
    const y = Math.max(360, Grid.ORIGIN_Y - 54)
    // Tumble step 3+ 是 cluster 链路的爽点 — 升级为更大 badge。Camera shake
    // 限制为 step ≥ 4 才触发，且强度减半（之前 0.005-0.012 太晃，配合 Fever
    // ignite 的 shake + 多 cascade 连续触发会让玩家头晕；降到 0.002-0.005 保留
    // "在状态"感同时不再晕）。
    const isMega = step >= 3
    const isShakingShake = step >= 4
    const c = this.add.container(x, y - 18).setDepth(610).setAlpha(0)
    if (isShakingShake) {
      // 力度分层：step 4 → 0.003, step 5 → 0.0035, step 6+ → 0.005 cap
      const intensity = Math.min(0.005, 0.0015 + step * 0.0005)
      this.cameras.main.shake(180, intensity)
    }

    const bg = this.add.graphics()
    bg.fillStyle(isMega ? 0x4d0a52 : 0x300638, 0.86)
    const w = isMega ? 430 : 370
    const hh = isMega ? 74 : 62
    bg.fillRoundedRect(-w / 2, -hh / 2, w, hh, 30)
    bg.lineStyle(isMega ? 6 : 4, isMega ? 0xffe066 : 0xffcf6b, isMega ? 0.92 : 0.72)
    bg.strokeRoundedRect(-w / 2, -hh / 2, w, hh, 30)
    bg.lineStyle(2, 0xffffff, isMega ? 0.46 : 0.34)
    bg.strokeRoundedRect(-w / 2 + 13, -hh / 2 + 9, w - 26, hh - 18, 22)
    c.add(bg)

    const label = isMega
      ? `★ TUMBLE × ${step} ★`
      : totalSteps > 1 ? `TUMBLE ${step}/${totalSteps}` : 'TUMBLE WIN'
    const title = this.add.text(-18, -8, label, {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: isMega ? '28px' : '24px',
      color: '#fff7c8',
      stroke: '#8a144f',
      strokeThickness: isMega ? 6 : 5,
      shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 4, fill: true },
    }).setOrigin(1, 0.5)
    c.add(title)

    const value = this.add.text(8, -8, `+${fmt(amount)}`, {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: isMega ? '32px' : '27px',
      color: '#ffd166',
      stroke: '#5b0b50',
      strokeThickness: isMega ? 6 : 5,
      shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 5, fill: true },
    }).setOrigin(0, 0.5)
    c.add(value)

    const sub = this.add.text(0, 17, 'CANDIES DROP AGAIN', {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: '12px',
      color: '#ffb7da',
      stroke: '#4a0634',
      strokeThickness: 3,
      letterSpacing: 2,
    }).setOrigin(0.5)
    c.add(sub)

    this.tweens.add({ targets: c, y, alpha: 1, duration: 160, ease: 'Cubic.easeOut' })
    this.tweens.add({
      targets: c,
      scale: 1.035,
      duration: 360,
      yoyo: true,
      repeat: 1,
      ease: 'Sine.inOut',
    })
    this.tweens.add({
      targets: c,
      y: y - 20,
      alpha: 0,
      delay: 760,
      duration: 220,
      ease: 'Sine.easeIn',
      onComplete: () => c.destroy(true),
    })
  }

  /** Auth 就绪后立刻拉真实余额——避免 HUD 显示 "—" 直到首 spin。
   *  失败安静回退（HUD 保持 "—"），首 spin 的 wallet_balance_after 还会兜底。 */
  private async hydrateRealBalance(): Promise<void> {
    try {
      const me = await HttpClient.get<{ balance: number }>('/api/v1/auth/me')
      if (typeof me.balance === 'number') {
        this.balance = me.balance
        this.balanceHydrated = true
        if (this.balanceText) {
          this.balanceText.setText(fmtBalance(this.balance, this.balanceHydrated))
          fitTextToWidth(this.balanceText, 265)
        }
      }
    } catch (err) {
      console.warn('[balance] hydrate failed', err)
    }
  }

  private maybeAutoSpin(): void {
    if (!this.autoSpinEnabled) return
    if (this.spinState !== 'idle') return
    if (this.balance < this.bet) {
      this.autoSpinEnabled = false  // 余额不足自动停
      return
    }
    if (this.fsRemaining > 0) return  // FS 模式由 runFsLoop 控制
    void this.startSpin()
  }

  private armAnimationDebug(): void {
    const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)
    if (!import.meta.env.DEV && !isLocalhost) return
    const href = window.location.href
    const params = new URLSearchParams(window.location.search)
    const level =
      params.get('winTest') ??
      (href.includes('winTest=jackpot') ? 'jackpot' :
       href.includes('winTest=super') ? 'super' :
       href.includes('winTest=mega') ? 'mega' :
       href.includes('winTest=big') ? 'big' :
       null)
    const animTest =
      params.get('animTest') ??
      (href.includes('animTest=freeSpins') ? 'freeSpins' :
       href.includes('animTest=winCells') ? 'winCells' :
       null)

    const amountByLevel = {
      big: 12_500,
      mega: 185_000,
      super: 1_280_000,
      jackpot: 18_888_888,
    } as const

    if (level === 'big' || level === 'mega' || level === 'super' || level === 'jackpot') {
      window.setTimeout(() => {
        if (GameContext.gameId() === 'candy') {
          void this.loadLazyAssets('win', CANDY_LAZY_WIN_ASSETS, CANDY_LAZY_WIN_AUDIO)
            .then(() => this.showWinPopup(level, amountByLevel[level]))
          return
        }
        void this.showWinPopup(level, amountByLevel[level])
      }, 450)
      return
    }

    if (animTest === 'freeSpins') {
      this.time.delayedCall(450, async () => {
        if (GameContext.gameId() === 'candy') {
          await this.loadLazyAssets('fs', CANDY_LAZY_FS_ASSETS, CANDY_LAZY_FS_AUDIO)
        }
        await this.showFreeSpinsEvent(10)
      })
      return
    }

    if (animTest === 'winCells') {
      this.time.delayedCall(650, () => {
        const cells: [number, number][] = [[1, 1], [2, 1], [3, 1], [3, 2], [4, 2], [4, 3]]
        const cleanupHl = this.applyWinHighlights(cells)
        const cleanupPop = this.popWinningSymbols(cells)
        const cleanupBurst = this.spawnCoinBurst(cells)
        this.time.delayedCall(2600, () => {
          cleanupHl()
          cleanupPop()
          cleanupBurst()
        })
      })
      return
    }

    if (animTest === 'cascade') {
      this.time.delayedCall(650, async () => {
        const after: SymbolKey[][] = [
          ['LEMON', 'CHERRY', 'RAINBOW', 'STRAWBERRY', 'BLUEBERRY'],
          ['CHERRY', 'LEMON', 'STRAWBERRY', 'BLUEBERRY', 'WATERMELON'],
          ['WATERMELON', 'RAINBOW', 'LEMON', 'STRAWBERRY', 'CHERRY'],
          ['STRAWBERRY', 'BLUEBERRY', 'LEMON', 'RAINBOW', 'WATERMELON'],
          ['BLUEBERRY', 'STRAWBERRY', 'CHERRY', 'LEMON', 'RAINBOW'],
          ['RAINBOW', 'WATERMELON', 'BLUEBERRY', 'STRAWBERRY', 'LEMON'],
        ]
        await this.runCascades([{
          round: 0,
          round_win: 1800,
          grid_after: after,
          clusters: [{
            payline_id: 'debug_cluster',
            symbol: 'STRAWBERRY',
            count: 7,
            win_amount: 1800,
            positions: [[1, 1], [2, 1], [3, 1], [3, 2], [4, 2], [4, 3], [5, 3]],
          }],
        }])
      })
    }
  }

  update(time: number, deltaMs: number): void {
    this.reelGrid?.update(time, deltaMs)
  }

  private centerSafeAreaCamera(): void {
    const extraW = Math.max(0, this.scale.width - Layout.CANVAS_W)
    const extraH = Math.max(0, this.scale.height - Layout.CANVAS_H)
    this.cameras.main.setScroll(-extraW / 2, -extraH / 2)
  }

  // ─── 背景：当前 game_id 的主 bg + 极轻 vignette ─
  private drawBackground(): void {
    this.backgroundObjects.forEach((obj) => obj.destroy())
    this.backgroundObjects = []

    const { CANVAS_W, CANVAS_H } = Layout
    const stageW = Math.max(CANVAS_W, this.scale.width)
    const stageH = Math.max(CANVAS_H, this.scale.height)
    const stageLeft = CANVAS_W / 2 - stageW / 2
    const stageTop = CANVAS_H / 2 - stageH / 2
    const cfg = GameContext.config()
    const bgKey = this.textures.exists(cfg.bgMainKey) ? cfg.bgMainKey : AssetKeys.bg.main
    const bg = this.add.image(CANVAS_W / 2, CANVAS_H / 2, bgKey).setOrigin(0.5)
    const tex = bg.texture.getSourceImage()
    const scale = Math.max(stageW / tex.width, stageH / tex.height)
    bg.setScale(scale).setDepth(-100)
    this.backgroundObjects.push(bg)

    // Dim 层：candy 保高饱和（~12%）；sakura 需要更暗的 base 让 Heat 70%+
    // breathing rim 有反差，否则整屏粉调时玩家分不清"还远"和"快了"。
    const isSakura = GameContext.gameId() === 'sakura'
    const dimAlpha = isSakura ? 0.30 : 0.12
    const dimColor = isSakura ? 0x140524 : 0x0a0420
    const dim = this.add
      .rectangle(CANVAS_W / 2, CANVAS_H / 2, stageW, stageH, dimColor, dimAlpha)
      .setOrigin(0.5)
      .setDepth(-90)
    this.backgroundObjects.push(dim)

    // 顶 + 底 vignette
    const dark = 0x05000a
    const top = this.add.graphics().setDepth(-89)
    top.fillGradientStyle(dark, dark, dark, dark, 0.45, 0.45, 0, 0)
    top.fillRect(stageLeft, stageTop, stageW, 420 + Math.max(0, -stageTop))
    this.backgroundObjects.push(top)

    const bot = this.add.graphics().setDepth(-89)
    bot.fillGradientStyle(dark, dark, dark, dark, 0, 0, 0.45, 0.45)
    bot.fillRect(stageLeft, 1480, stageW, stageTop + stageH - 1480)
    this.backgroundObjects.push(bot)
  }

  // ─── 顶部条（4 round icon buttons：back / settings / info / close）──
  private drawTopBar(): void {
    const cfg = GameContext.config()
    if (cfg.id === 'sakura') {
      this.drawSakuraTopBar()
      return
    }
    const y = 68
    const size = 76
    const startX = 70
    const gap = 104
    const icons: { label: string; cb: () => void }[] = [
      { label: '<', cb: () => this.goToGameLobby() },
      { label: 'O', cb: () => this.showSettingsPanel() },
      { label: 'i', cb: () => this.showInfoPanel() },
      { label: 'X', cb: () => this.goToGameLobby() },
    ]
    icons.forEach((it, i) => {
      this.makeRoundIconButton(startX + i * gap, y, size, it.label, it.cb)
    })
  }


  // 注：Daily/Shop/Leaderboard/Paytable/Settings 入口移到 Lobby 后由 LobbyScene 调用 showSimplePopup。

  // ─── Jackpot strip：GRAND 宽槽 + MAJOR/MINOR/MINI 小槽 ─
  private drawJackpotHeader(): void {
    const cfg = GameContext.config()
    if (cfg.themeAssets?.jackpotHeaderKey) {
      this.drawJackpotHeaderThemed()
      return
    }
    const cx = Layout.CANVAS_W / 2
    const cy = 268
    const sourceW = 1512
    const sourceH = 416
    const displayW = Layout.CANVAS_W * 0.94
    const scale = displayW / sourceW
    const jackpot = this.add.image(cx, cy, AssetKeys.ui.gen_jackpot_grand_wide).setOrigin(0.5)
    jackpot.setDisplaySize(displayW, sourceH * scale)

    // GRAND 是商业卖点，所以它的 value well 明显更宽，可容纳千万级金额。
    const slots = [
      { srcX: 302, value: 8_250_000, fontSize: 44, maxW: 500, color: '#fff4b8' },
      { srcX: 754, value: 125_400, fontSize: 31, maxW: 250, color: '#ffffff' },
      { srcX: 1058, value: 5_820, fontSize: 28, maxW: 250, color: '#f6fbff' },
      { srcX: 1323, value: 880, fontSize: 26, maxW: 220, color: '#f7ffd6' },
    ] as const
    const valueY = cy + (282 - sourceH / 2) * scale

    this.jackpotEntries = []
    for (const s of slots) {
      const x = cx + (s.srcX - sourceW / 2) * scale
      const txt = this.add.text(x, valueY, `$${fmt(s.value)}`, {
        fontFamily: 'Arial Black, Impact, sans-serif',
        fontSize: `${s.fontSize}px`,
        color: s.color,
        stroke: '#41003f',
        strokeThickness: 5,
        shadow: { offsetX: 0, offsetY: 2, color: '#000', blur: 4, fill: true },
      }).setOrigin(0.5)
      const boxDispW = s.maxW * scale
      fitTextToWidth(txt, boxDispW)
      this.jackpotEntries.push({ text: txt, value: s.value, boxDispW, boxDispH: 0, prefix: '$' })
    }

    this.jackpotTickerEvent = this.time.addEvent({
      delay: 2600,
      loop: true,
      callback: () => this.tickJackpots(),
    })
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.jackpotTickerEvent?.remove(false)
      this.jackpotTickerEvent = null
    })
  }

  /** Jackpot 滚动：4 档各加一个微小随机增量并 tween 到位 */
  private tickJackpots(): void {
    const rates = [0.00012, 0.00045, 0.0014, 0.003]
    for (let i = 0; i < this.jackpotEntries.length; i++) {
      const e = this.jackpotEntries[i]!
      const rate = rates[i] ?? 0.0005
      const inc = Math.max(1, Math.floor(e.value * rate * (0.6 + Math.random() * 0.8)))
      const target = e.value + inc
      const counter = { v: e.value }
      this.tweens.add({
        targets: counter,
        v: target,
        duration: 900,
        ease: 'Cubic.easeOut',
        onUpdate: () => {
          e.text.setText(`${e.prefix}${fmt(Math.floor(counter.v))}`)
          fitTextToWidth(e.text, e.boxDispW)
        },
        onComplete: () => {
          e.value = target
          e.text.setText(`${e.prefix}${fmt(target)}`)
          fitTextToWidth(e.text, e.boxDispW)
        },
      })
    }
  }

  // ─── Reel zone：每个 game 用自己的 frame 素材 ───
  private drawReelZone(): void {
    // Frame 包住 grid + 一圈装饰 padding
    const cfg = GameContext.config()
    if (cfg.id === 'sakura') {
      Grid.ORIGIN_Y += 86
    }
    const cx = Grid.ORIGIN_X + GridSize.WIDTH / 2
    const cy = Grid.ORIGIN_Y + GridSize.HEIGHT / 2
    const frameKey = cfg.themeAssets?.reelFrameKey && this.textures.exists(cfg.themeAssets.reelFrameKey)
      ? cfg.themeAssets.reelFrameKey
      : AssetKeys.frame.grid_6x5
    const padX = cfg.id === 'sakura' ? 112 : 80
    const padY = cfg.id === 'sakura' ? 154 : 80
    const dispW = GridSize.WIDTH + padX
    const dispH = GridSize.HEIGHT + padY
    if (cfg.id === 'sakura') {
      this.add.rectangle(cx, cy, GridSize.WIDTH + 38, GridSize.HEIGHT + 38, 0x26052f, 0.46)
        .setOrigin(0.5)
        .setDepth(-1)
      const reelOutline = this.add.graphics().setDepth(3)
      reelOutline.lineStyle(7, 0xfde047, 0.82)
      reelOutline.strokeRoundedRect(Grid.ORIGIN_X - 15, Grid.ORIGIN_Y - 15, GridSize.WIDTH + 30, GridSize.HEIGHT + 30, 22)
      reelOutline.lineStyle(3, 0xec4899, 0.72)
      reelOutline.strokeRoundedRect(Grid.ORIGIN_X - 22, Grid.ORIGIN_Y - 22, GridSize.WIDTH + 44, GridSize.HEIGHT + 44, 28)
    }
    const frame = this.add.image(cx, cy, frameKey).setOrigin(0.5)
    frame.setDisplaySize(dispW, dispH)
    frame.setDepth(cfg.id === 'sakura' ? 2 : 0)

    this.reelGrid = new ReelGrid(this)
  }

  // ─── HUD：candy `ui_balance_pill` (左) + Win 文字 (右上) ─────
  private drawHud(): void {
    const cfg = GameContext.config()
    if (cfg.themeAssets?.controlBarKey) {
      this.drawHudThemed()
      return
    }
    const hudY = 1418   // 给下方 SPIN 留出间距，避免手机端 WIN 与 SPIN 贴在一起
    const balCX = 305
    const balShell = this.add.image(balCX, hudY, AssetKeys.ui.gen_balance_pill_wide).setOrigin(0.5)
    balShell.setPosition(balCX, hudY)
    balShell.setDisplaySize(575, 138)
    this.balanceText = this.add.text(balCX + 203, hudY + 7, fmtBalance(this.balance, this.balanceHydrated), {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: '40px',
      color: '#ffffff',
      stroke: '#41003f',
      strokeThickness: 6,
      shadow: { offsetX: 0, offsetY: 3, color: '#1a0420', blur: 5, fill: true },
      align: 'right',
    }).setOrigin(1, 0.5)
    fitTextToWidth(this.balanceText, 265)

    const winCX = 840
    const winShell = this.add.image(winCX, hudY, AssetKeys.ui.gen_win_pill).setOrigin(0.5)
    winShell.setDisplaySize(340, 109)
    this.winText = this.add.text(winCX + 76, hudY + 5, '0', {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: '34px',
      color: '#ffd166',
      stroke: '#41003f',
      strokeThickness: 6,
      shadow: { offsetX: 0, offsetY: 3, color: '#1a0420', blur: 5, fill: true },
    }).setOrigin(0.5)
    fitTextToWidth(this.winText, 130)
  }

  // ─── 控制栏：Total Bet 素材 (左，- / + 已烘焙) + SPIN 素材 (右) ─
  private drawControlBar(): void {
    const cfg = GameContext.config()
    if (cfg.themeAssets?.controlBarKey) {
      this.drawControlBarThemed()
      return
    }
    const betCX = 292
    const betCY = 1608   // 真机 Safari 底部安全区会吃掉最下方，控制行整体上移
    const pillW = 470
    const pillH = 145
    const betShell = this.add.image(betCX, betCY, AssetKeys.ui.gen_total_bet_pill).setOrigin(0.5)
    betShell.setDisplaySize(pillW, pillH)

    // The generated total-bet value well is slightly left/up from the full asset center.
    this.betText = this.add.text(betCX - 10, betCY + 14, fmt(this.bet), {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: '40px',
      color: '#ffffff',
      stroke: '#41003f',
      strokeThickness: 6,
    }).setOrigin(0.5)
    fitTextToWidth(this.betText, 220)

    const sideW = pillW * 0.21

    // - / + hit zones
    const minusZone = this.add
      .zone(betCX - pillW / 2 + sideW / 2, betCY, sideW, pillH)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
    minusZone.on('pointerup', () => this.changeBet(-1))
    const plusZone = this.add
      .zone(betCX + pillW / 2 - sideW / 2, betCY, sideW, pillH)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
    plusZone.on('pointerup', () => this.changeBet(+1))

    const spinCX = Layout.CANVAS_W - 188
    const spinCY = 1608   // 跟 betCY 对齐；给 iOS 底部安全区留出空间
    this.spinBtn = this.add.image(spinCX, spinCY, AssetKeys.ui.gen_spin_button).setOrigin(0.5)
    fitTo(this.spinBtn, 244, 244)
    this.spinBtnBaseScale = this.spinBtn.scale
    this.spinBtn.setInteractive({ useHandCursor: true })
    this.spinBtn.on('pointerdown', () => this.onSpinPressed())
    this.spinBtn.on('pointerup', () => this.onSpinReleased())
    this.startSpinIdleMotion()
  }

  // 底部工具托盘已删除 — Auto/Turbo/MaxBet/BuyBonus 入口后续在 Lobby 提供

  // ─── BGM（防叠加固） ─────────────────────────────────
  private armBgm(): void {
    const play = () => {
      // 入口防叠：scene 的 BGM 已在播 / 全局已有 BGM 实例 → 跳过
      const w = window as unknown as { __NCF_BGM_PLAYING__?: boolean }
      if (w.__NCF_BGM_PLAYING__) return
      if (this.bgm && this.isSoundPlaying(this.bgm)) return

      // 兜底：先 stopAll 一遍，确保单 BGM
      this.sound.stopAll()
      const cfg = GameContext.config()
      const bgmKey = this.cache.audio.exists(cfg.bgmMainKey) ? cfg.bgmMainKey : AssetKeys.audio.bgm_main
      this.bgm = this.sound.add(bgmKey, { loop: true, volume: 0.32 })
      this.bgm.play()
      w.__NCF_BGM_PLAYING__ = true
    }
    this.input.once('pointerdown', play)
    // Live 直播间 watch 模式下 SPIN 被 disable，玩家不会点 canvas → Phaser
    // 的 input pointerdown 永远不 fire → BGM 永远不响。再挂一个 window 级 listener，
    // 让 iframe 内任何位置的 pointer 都能解锁。play() 自带 __NCF_BGM_PLAYING__
    // 防叠 → 4 人房 / solo 不会因为这个二次启动 BGM。
    window.addEventListener('pointerdown', play, { once: true })
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () =>
      window.removeEventListener('pointerdown', play),
    )
  }

  private isSoundPlaying(s: Phaser.Sound.BaseSound): boolean {
    return (s as unknown as { isPlaying?: boolean }).isPlaying ?? false
  }

  /** Watch mode 进来时主动尝试解锁 audio context + 启动 BGM。
   *  浏览器 autoplay policy 通常需要 user gesture；父页面的 click 在 Chrome 里能 cross-frame 算 user activation。 */
  private tryUnlockAudio(): void {
    try {
      const ctx = (this.sound as unknown as { context?: AudioContext }).context
      if (ctx && ctx.state === 'suspended') void ctx.resume()
    } catch { /* ok */ }
    const w = window as unknown as { __NCF_BGM_PLAYING__?: boolean }
    if (w.__NCF_BGM_PLAYING__) return
    if (this.bgm && this.isSoundPlaying(this.bgm)) return
    try {
      this.sound.stopAll()
      const cfg = GameContext.config()
      const bgmKey = this.cache.audio.exists(cfg.bgmMainKey) ? cfg.bgmMainKey : AssetKeys.audio.bgm_main
      this.bgm = this.sound.add(bgmKey, { loop: true, volume: 0.32 })
      this.bgm.play()
      w.__NCF_BGM_PLAYING__ = true
    } catch { /* ok 浏览器 block 就 fail silently */ }
  }

  private cleanupAudio(): void {
    const w = window as unknown as { __NCF_BGM_PLAYING__?: boolean }
    w.__NCF_BGM_PLAYING__ = false
    this.stopAnticipationPulse()
    if (this.bgm) {
      try {
        this.bgm.stop()
      } catch {
        /* ok */
      }
      this.bgm = null
    }
    try {
      this.sound.stopAll()
    } catch {
      /* ok */
    }
  }

  /**
   * SFX 串行播放：开始下一个前停掉所有非 BGM 的声音
   * 任何时候至多有：BGM(可暂停) + 1 个 SFX
   */
  private playSfx(key: string, volume = 0.5): void {
    this.stopAllNonBgm()
    try {
      this.sound.play(key, { volume })
    } catch {
      /* ok */
    }
  }

  /**
   * 当前 game 的 SFX key 解析器：每个游戏的 manifest 提供自己的 SFX，
   * 不再 hardcode candy。sakura 走自己的 J-SFX*，candy 走 nc_sfx_*。
   */
  private sfx(name:
    | 'spin_tap' | 'reels_start' | 'reel_stop'
    | 'small_win' | 'big_win_count'
    | 'free_spins_trigger' | 'free_spin_start'
    | 'cluster_detected' | 'candy_tumble'
    | 'wild_hit'
    | 'fever_ignite' | 'fever_settlement',
  ): string {
    const cfg = GameContext.config()
    // 任何 game 只要给了 themeAssets，就走它自己的 audio key（candy 不给 themeAssets，走原 AssetKeys）
    if (cfg.themeAssets) {
      const t = cfg.themeAssets
      switch (name) {
        case 'spin_tap':           return cfg.sfxSpinTapKey
        case 'reels_start':        return t.sfxReelsStartKey ?? cfg.sfxSpinTapKey
        case 'reel_stop':          return cfg.sfxReelStopKey
        case 'small_win':          return cfg.sfxSmallWinKey
        case 'big_win_count':      return cfg.sfxBigWinCountKey
        case 'free_spins_trigger': return t.sfxFreeSpinsTriggerKey ?? cfg.sfxBigWinCountKey
        case 'free_spin_start':    return t.sfxFreeSpinStartKey ?? cfg.sfxBigWinCountKey
        case 'cluster_detected':   return cfg.sfxSmallWinKey
        case 'candy_tumble':       return cfg.sfxSmallWinKey
        case 'wild_hit':           return t.sfxWildHitKey ?? cfg.sfxSmallWinKey
        // Fever cues — sakura doesn't ship its own; fall back to candy keys
        // (which load if the user has played candy in the same session,
        // else playSfx no-ops gracefully when the key isn't in cache).
        case 'fever_ignite':       return AssetKeys.audio.sfx_fever_ignite
        case 'fever_settlement':   return AssetKeys.audio.bgm_fever_settlement
      }
    }
    switch (name) {
      case 'spin_tap':           return AssetKeys.audio.sfx_spin_tap
      case 'reels_start':        return AssetKeys.audio.sfx_reels_start
      case 'reel_stop':          return AssetKeys.audio.sfx_reel_stop
      case 'small_win':          return AssetKeys.audio.sfx_small_win
      case 'big_win_count':      return AssetKeys.audio.sfx_big_win_count
      case 'free_spins_trigger': return AssetKeys.audio.sfx_free_spins_trigger
      case 'free_spin_start':    return AssetKeys.audio.sfx_free_spin_start
      case 'cluster_detected':   return AssetKeys.audio.sfx_cluster_detected
      case 'candy_tumble':       return AssetKeys.audio.sfx_candy_tumble
      case 'wild_hit':           return AssetKeys.audio.sfx_small_win
      case 'fever_ignite':       return AssetKeys.audio.sfx_fever_ignite
      case 'fever_settlement':   return AssetKeys.audio.bgm_fever_settlement
    }
  }

  /**
   * 按需 fetch 一组 lazy 资源（图片 + 音频）。已存在的 key 跳过。
   * 用于 Big Win / Free Spins 全屏 overlay：boot 时不 preload，触发时才 fetch。
   *
   * 同一 key 并发触发会被去重（pendingLazyLoads 缓存 Promise）。
   */
  private pendingLazyLoads = new Map<string, Promise<void>>()
  private loadLazyAssets(
    cacheTag: string,
    images: readonly { key: string; path: string }[],
    audio: readonly { key: string; path: string }[],
  ): Promise<void> {
    const cached = this.pendingLazyLoads.get(cacheTag)
    if (cached) return cached

    let needLoad = false
    for (const a of images) {
      if (!this.textures.exists(a.key)) {
        this.load.image(a.key, a.path)
        needLoad = true
      }
    }
    for (const a of audio) {
      if (!this.cache.audio.exists(a.key)) {
        this.load.audio(a.key, a.path)
        needLoad = true
      }
    }
    if (!needLoad) {
      const done = Promise.resolve()
      this.pendingLazyLoads.set(cacheTag, done)
      return done
    }

    const p = new Promise<void>((resolve) => {
      this.load.once(Phaser.Loader.Events.COMPLETE, () => resolve())
      this.load.start()
    })
    this.pendingLazyLoads.set(cacheTag, p)
    return p
  }

  /** 停掉所有非 BGM 的声音 */
  private stopAllNonBgm(): void {
    try {
      const sm = this.sound as unknown as { sounds?: Phaser.Sound.BaseSound[] }
      const list = sm.sounds ?? []
      for (const s of list) {
        if (s === this.bgm) continue
        const playing = (s as unknown as { isPlaying?: boolean }).isPlaying
        if (playing) {
          try {
            s.stop()
          } catch {
            /* ok */
          }
        }
      }
    } catch {
      /* ok */
    }
  }

  /** Spin 期间 BGM 暂停 */
  private pauseBgm(): void {
    if (!this.bgm) return
    try {
      if (this.isSoundPlaying(this.bgm)) this.bgm.pause()
    } catch {
      /* ok */
    }
  }

  /** Spin 结束 BGM 恢复 */
  private resumeBgm(): void {
    if (!this.bgm) return
    try {
      const s = this.bgm as Phaser.Sound.BaseSound & { isPaused?: boolean; resume?: () => void }
      if (s.isPaused) s.resume?.()
    } catch {
      /* ok */
    }
  }

  // ─── Spin 流程（状态机） ────────────────────────────
  private canStartSpin(): boolean {
    return (
      this.spinState === 'idle' &&
      !this.reelGrid.isSpinning() &&
      this.balance >= this.bet
    )
  }

  private onSpinPressed(): void {
    this.lastInteractionAt = Date.now()
    if (this.thermalQuiet) this.exitThermalQuiet()
    if (this.spinState !== 'idle') return
    this.stopSpinIdleMotion()
    this.tweens.killTweensOf(this.spinBtn)
    this.tweens.add({
      targets: this.spinBtn,
      scale: this.spinBtnBaseScale * 0.9,
      angle: -5,
      duration: 80,
      ease: 'Cubic.easeOut',
    })
    // Tactile audio at press, not after release/startSpin —— the perceived gap
    // between finger-down and feedback should be ≤ 80ms. Previously the
    // spin_tap played inside startSpin() which fires ~200ms after pointerdown,
    // so the click felt mute. ADR-035 #1 timing.
    if (this.canStartSpin()) {
      this.playSfx(this.sfx('spin_tap'), 0.55)
    }
  }

  private onSpinReleased(): void {
    this.tweens.killTweensOf(this.spinBtn)
    if (GameContext.gameId() === 'candy') {
      this.cameras.main.shake(90, 0.0012)
    }
    this.tweens.add({
      targets: this.spinBtn,
      scale: this.spinBtnBaseScale * 1.08,
      angle: 4,
      duration: 120,
      yoyo: true,
      ease: 'Back.easeOut',
    })
    if (!this.canStartSpin()) {
      this.time.delayedCall(260, () => this.startSpinIdleMotion())
      return
    }
    this.startSpin()
  }

  private async startSpin(): Promise<void> {
    this.lastInteractionAt = Date.now()
    if (this.thermalQuiet) this.exitThermalQuiet()
    this.spinState = 'spinning'
    this.spinBtn.disableInteractive()
    this.startSpinRotation()

    this.pauseBgm()
    // spin_tap moved to onSpinPressed for tighter haptics; reels_start covers
    // the spin-engaged audio cue here.
    this.time.delayedCall(120, () => this.playSfx(this.sfx('reels_start'), 0.45))

    const inFsMode = this.fsRemaining > 0

    // 乐观扣 bet（FS 期间不扣，服务器也不扣）
    if (!inFsMode) {
      this.balance -= this.bet
      this.balanceText.setText(fmt(this.balance))
      fitTextToWidth(this.balanceText, 265)
    }
    // Win 文字归零规则：
    //  - Base game (付费 spin)：归零（玩家付了钱，开始新的中奖累计）
    //  - FS mode：保留前几次 FS spin 的累积，新 cascade 在此基础上叠加
    if (!inFsMode) {
      this.winText.setText('0')
      fitTextToWidth(this.winText, 130)
    } else {
      // 保留显示 fsTotalWin（前几次 FS spin 的累计），cascade 增量从这里加
      this.winText.setText(fmt(this.fsTotalWin))
      fitTextToWidth(this.winText, 130)
    }

    // ★ 乐观 spin：reels 立刻转起来，不等后端返回再开始动画。
    // 手机端 RTT 800-1500ms，原本要等 fetch 才能看到 reels 动 → 玩家感觉「按了 spin 卡 1 秒」。
    // 现在 reels 先转，fetch 并行进行；等 reels 转够最小时长后再 await result 决定停在哪。
    this.reelGrid.spin()
    const constMs = this.turboOn ? 220 : 540

    const resultPromise = this.requestSpinResult(inFsMode).catch((err): SpinResponse | null => {
      console.error('[spin] failed', err)
      return null
    })

    await delay(constMs)
    const result = await resultPromise

    if (!result) {
      // 后端失败：回滚乐观扣款 + 把 reels 停回原盘面（视觉上「啥也没发生」）
      if (!inFsMode) {
        this.balance += this.bet
        this.balanceText.setText(fmt(this.balance))
        fitTextToWidth(this.balanceText, 265)
      }
      // 让 reels 自然落到随机盘面（视觉收尾，不影响余额回滚）
      try { await this.reelGrid.stop(generateRandomGrid()) } catch { /* ok */ }
      this.stopSpinRotation()
      this.spinBtn.setInteractive({ useHandCursor: true })
      this.spinState = 'idle'
      this.stopAllNonBgm()
      this.resumeBgm()
      return
    }

    // 列停 SFX — candy 现只有一个 reel_stop 音效，每列错开播放
    const stopGap = this.turboOn ? 70 : 160
    for (let c = 0; c < Grid.COLS; c++) {
      this.time.delayedCall(c * stopGap, () =>
        this.playSfx(this.sfx('reel_stop'), 0.45),
      )
    }

    // ── 关键 try/catch：post-result 任意异常都要保证 spinState 回到 idle，
    //    否则 SPIN 按钮永远 disabled，BGM 永远 paused，玩家看到「转个不停 + 没声音」。
	    try {
	      await this.reelGrid.stop(result.grid)
	      this.spinState = 'evaluating'
	      this.balance = result.wallet_balance_after
	      this.balanceHydrated = true
	      this.balanceText.setText(fmt(this.balance))
	      fitTextToWidth(this.balanceText, 265)
      const roomAfter = result.room_state_after
      if (roomAfter && typeof roomAfter === 'object' && 'fever_active' in roomAfter) {
        const r = roomAfter as { fever_active?: unknown; fever_ends_at?: unknown }
        this.setCandyFeverLive(
          r.fever_active === true,
          typeof r.fever_ends_at === 'string' ? r.fever_ends_at : null,
        )
      }

	      if (result.total_win > 0) {
	        this.spinState = 'showingWin'
	      const winKey =
	        result.display_hints.win_level === 'small'
	          ? this.sfx('small_win')
	          : this.sfx('big_win_count')
	        this.playSfx(winKey, 0.6)

      // cluster_tumble: 跑级联；walking_wilds: 跑 walking-wild respin steps; 其他: win_lines 高亮
      // 注意：cluster_tumble / walking_wilds 期间 winText 由各自 runner 累加
      if (result.mechanic === 'cluster_tumble' && result.cascades && result.cascades.length > 0) {
        // FS 模式：cascade 起点 = fsTotalWin（前几次 FS spin 累积），新 cascade 叠上去
        const startingTally = inFsMode ? this.fsTotalWin : 0
        await this.runCascades(result.cascades, startingTally)
        const finalDisplay = inFsMode && result.fs_state ? result.fs_state.total_win : result.total_win
        this.winText.setText(fmt(finalDisplay))
        fitTextToWidth(this.winText, 130)
      } else if (result.mechanic === 'walking_wilds') {
        // Sakura: 先高亮 base spin 的 win lines（如有），然后跑 walking wild respin steps
        const startingTally = inFsMode ? this.fsTotalWin : 0
        // base spin 的 win_lines 是 line0..line29_s0；step1+ 的 lines 也在同一 win_lines 数组里
        const baseLines = result.win_lines.filter((ln) => ln.payline_id.endsWith('_s0'))
        const baseWin = baseLines.reduce((sum, ln) => sum + ln.win_amount, 0)
        if (baseLines.length > 0) {
          const cells = baseLines.flatMap((ln) => ln.positions)
          const cleanupHl = this.applyWinHighlights(cells)
          const cleanupPop = this.popWinningSymbols(cells)
          const cleanupBurst = this.spawnCoinBurst(cells)
          this.showPayoutToast('LINE WIN', baseWin)
          this.tweenWinTextTo(startingTally + baseWin, 500)
          await delay(800)
          cleanupHl()
          cleanupPop()
          cleanupBurst()
        }
        await this.runWalkingWildSteps(result, startingTally + baseWin)
        const finalDisplay = inFsMode && result.fs_state ? result.fs_state.total_win : result.total_win
        this.winText.setText(fmt(finalDisplay))
        fitTextToWidth(this.winText, 130)
      } else {
        // 非级联：一次性 animate + 高亮 win_lines
        this.animateWinCounter(result.total_win, 1100)
        if (result.win_lines.length > 0) {
          const cells = result.win_lines.flatMap((ln) => ln.positions)
          const cleanupHl = this.applyWinHighlights(cells)
          const cleanupPop = this.popWinningSymbols(cells)
          const cleanupBurst = this.spawnCoinBurst(cells)
          this.showPayoutToast('WIN', result.total_win)
          await delay(1500)
          cleanupHl()
	          cleanupPop()
	          cleanupBurst()
	        }
	      }

	      this.showCandyFeverWinToast(result.total_win)

	      const lvl = result.display_hints.win_level
	      if (lvl === 'big' || lvl === 'mega' || lvl === 'super' || lvl === 'jackpot') {
	        // sakura 的 win panels 已在主 manifest 预加载（IMG-085-088），不需要 candy lazy
	        if (GameContext.gameId() === 'candy') {
	          await this.loadLazyAssets('win', CANDY_LAZY_WIN_ASSETS, CANDY_LAZY_WIN_AUDIO)
	        }
	        await this.showWinPopup(lvl, result.total_win)
	      }
	    }

    this.stopSpinRotation()
    this.spinState = 'idle'

    this.stopAllNonBgm()
    this.resumeBgm()

    // ── FS 状态机推进 ──────────────────────────────────────
    const fs = result.fs_state ?? null

    // Case A: 本次是 base spin 触发了 FS（fs_state 出现，但 inFsMode=false）
    if (!inFsMode && fs && fs.remaining > 0) {
      // sakura 的 FS panel + bg 已在主 manifest 预加载（IMG-089 + IMG-015），不需要 candy lazy
      if (GameContext.gameId() === 'candy') {
        await this.loadLazyAssets('fs', CANDY_LAZY_FS_ASSETS, CANDY_LAZY_FS_AUDIO)
      }
      await this.showFreeSpinsEvent(fs.remaining)
      this.setFsRemaining(fs.remaining)
      this.fsTotalWin = 0
      this.fsAccumulator = 0
      this.showFsCounter()
      // 启动 FS auto-loop
      this.time.delayedCall(800, () => void this.runFsLoop())
      return
    }

    // Case B: 本次是 FS spin（inFsMode=true）
    if (inFsMode && fs) {
      this.setFsRemaining(fs.remaining)
      this.fsTotalWin = fs.total_win
      this.fsAccumulator = fs.accumulator
      this.updateFsCounter()
      if (fs.ended) {
        // 显示 FS 结算 popup（this.fsTotalWin 已镜像 fs.total_win）
        await this.showFsSummary(this.fsTotalWin)
        this.hideFsCounter()
        this.spinBtn.setInteractive({ useHandCursor: true })
        return
      }
      // 还有 FS spin → 自动接下一发
      this.time.delayedCall(900, () => void this.runFsNext())
      return
    }

    // Case B': FS desync recovery — client thinks it's in FS mode but server
    // returned no fs_state. This happens when (a) backend restarted mid-FS and
    // wiped in-memory sessions, (b) sweepStaleFsSessions purged a 30min+ idle
    // session, or (c) any other case where server lost the session. Without
    // this branch the FS counter sticks forever (e.g. "FREE SPINS 4×12") and
    // every subsequent SPIN is processed as a base spin (deducting bet) while
    // the user thinks they're still in FS. Force-end FS client-side.
    if (inFsMode && !fs) {
      console.warn('[FS desync] inFsMode=true but server returned null fs_state — forcing client-side FS end')
      this.setFsRemaining(0)
      this.fsTotalWin = 0
      this.fsAccumulator = 0
      try { this.hideFsCounter() } catch { /* ignore */ }
      this.spinBtn.setInteractive({ useHandCursor: true })
      try {
        window.parent?.postMessage({ type: 'sss-spin-done', win: result.total_win }, '*')
      } catch { /* ignore */ }
      return
    }

    // Case C: 普通 base spin，无 FS → 恢复 SPIN 按钮 + 可能 auto-spin
    this.spinBtn.setInteractive({ useHandCursor: true })
    // 通知 parent player-web：本次 spin 庆祝动画已完成，可以更新顶端 self 累计 win 显示
    try {
      window.parent?.postMessage({ type: 'sss-spin-done', win: result.total_win }, '*')
    } catch { /* ignore */ }
    if (this.autoSpinEnabled && this.balance >= this.bet) {
      this.time.delayedCall(this.autoSpinDelayMs, () => this.maybeAutoSpin())
    }
    } catch (err) {
      // Post-result 异常恢复：把所有状态拉回 idle，保证 SPIN 重新可点 + BGM 恢复 + 没卡住。
      // 不丢日志，方便事后查（玩家若反馈「卡住」，console 里能看到具体异常 + 调用栈）。
      console.error('[spin] post-result handler crashed; recovering to idle', err)
      try { this.stopSpinRotation() } catch { /* ignore */ }
      this.spinState = 'idle'
      try { this.spinBtn.setInteractive({ useHandCursor: true }) } catch { /* ignore */ }
      try { this.stopAllNonBgm() } catch { /* ignore */ }
      try { this.resumeBgm() } catch { /* ignore */ }
      // 若 crash 时正在 FS 中，强制结束 FS 防止再 auto-loop 触发同一异常
      if (this.fsRemaining > 0) {
        this.setFsRemaining(0)
        this.fsTotalWin = 0
        this.fsAccumulator = 0
        try { this.hideFsCounter() } catch { /* ignore */ }
      }
    }
  }

  private async requestSpinResult(inFsMode: boolean): Promise<SpinResponse> {
    // Sakura 之前走 demo spin（不打 backend），导致 room state 不更新；改成全部走 backend。
    // 若离线 fallback 需要，可再加 ?demo=1 query 显式启用。
    void inFsMode
    return SpinService.spin({
      game_id: GameContext.gameId(),
      bet_amount: this.bet,
      boost: this.turboOn,
    })
  }

  // ─── FS auto-loop helpers ───────────────────────────────
  private async runFsLoop(): Promise<void> {
    // 第一次进入 FS round；触发第一发 FS spin（其余靠 startSpin 末尾自动衔接）
    if (this.fsRemaining > 0 && this.spinState === 'idle') {
      await this.startSpin()
    }
  }

  private async runFsNext(): Promise<void> {
    if (this.fsRemaining > 0 && this.spinState === 'idle') {
      await this.startSpin()
    }
  }

  private showFsCounter(): void {
    if (this.fsCounterText) this.fsCounterText.destroy()
    const cx = Layout.CANVAS_W / 2
    // HUD pill 中心约 y=1455，reel 视觉底部约 y=1300。把 FS 计数挪到两者之间靠上的空白带，
    // 不再压在 total_bet pill 上（之前 y≈1465 直接撞 "99" 数字）。
    const y = 1340
    this.fsCounterText = this.add
      .text(cx, y, this.formatFsCounter(), {
        fontFamily: 'Arial Black, Impact, sans-serif',
        fontSize: '40px',
        color: '#fde047',
        stroke: '#5b1a8e',
        strokeThickness: 6,
        shadow: { offsetX: 0, offsetY: 3, color: '#000', blur: 5, fill: true },
      })
      .setOrigin(0.5)
      .setDepth(800)
  }

  private updateFsCounter(): void {
    if (!this.fsCounterText) {
      this.showFsCounter()
      return
    }
    const txt = this.fsCounterText
    txt.setText(this.formatFsCounter())
    // Sweet Bonanza-style：每次更新（FS 减 1 或 accumulator 涨）pulse 一下吸引注意
    this.tweens.killTweensOf(txt)
    txt.setScale(1)
    this.tweens.add({
      targets: txt,
      scale: 1.18,
      duration: 140,
      ease: 'Cubic.easeOut',
      yoyo: true,
      onComplete: () => txt.setScale(1),
    })
    // 颜色闪一下（黄 → 白 → 黄）
    txt.setColor('#ffffff')
    this.time.delayedCall(180, () => {
      try { txt.setColor('#fde047') } catch { /* ok */ }
    })
  }

  private hideFsCounter(): void {
    this.fsCounterText?.destroy()
    this.fsCounterText = null
    this.setFsRemaining(0)
    this.fsTotalWin = 0
    this.fsAccumulator = 0
  }

  private formatFsCounter(): string {
    const accStr = this.fsAccumulator > 0 ? ` ×${this.fsAccumulator}` : ''
    return `FREE SPINS  ${this.fsRemaining}${accStr}`
  }

  /** FS round 结束后的总赢额结算弹窗 */
  private showFsSummary(totalWin: number): Promise<void> {
    return new Promise((resolve) => {
      const { CANVAS_W, CANVAS_H } = Layout
      const cx = CANVAS_W / 2
      const cy = CANVAS_H / 2

      const dim = this.add
        .rectangle(cx, cy, CANVAS_W, CANVAS_H, 0x05000a, 0.92)
        .setOrigin(0.5)
        .setDepth(2000)

      const title = this.add
        .text(cx, cy - 200, 'FREE SPINS COMPLETE', {
          fontFamily: 'Arial Black, Impact, sans-serif',
          fontSize: '60px',
          color: '#fff7c8',
          stroke: '#5b1a8e',
          strokeThickness: 8,
        })
        .setOrigin(0.5)
        .setDepth(2003)

      const winLabel = this.add
        .text(cx, cy - 30, 'TOTAL WIN', {
          fontFamily: 'Arial, sans-serif',
          fontSize: '32px',
          color: '#fce7f3',
        })
        .setOrigin(0.5)
        .setDepth(2003)

      const winAmount = this.add
        .text(cx, cy + 60, '$' + fmt(totalWin), {
          fontFamily: 'Arial Black, Impact, sans-serif',
          fontSize: '110px',
          color: '#fde047',
          stroke: '#5b1a8e',
          strokeThickness: 10,
          shadow: { offsetX: 0, offsetY: 6, color: '#000', blur: 8, fill: true },
        })
        .setOrigin(0.5)
        .setDepth(2003)
      winAmount.setAlpha(0).setScale(0.4)
      this.tweens.add({
        targets: winAmount,
        alpha: 1,
        scale: 1,
        duration: 540,
        delay: 200,
        ease: 'Back.easeOut',
      })

      this.playSfx(this.sfx('big_win_count'), 0.7)

      let cleaned = false
      const cleanup = () => {
        if (cleaned) return
        cleaned = true
        // Resolve first so a downstream destroy throw can't hang the await.
        try { resolve() } catch { /* ignore */ }
        try { this.tweens.killTweensOf(winAmount) } catch { /* ignore */ }
        try { winAmount.destroy() } catch { /* ignore */ }
        try { winLabel.destroy() } catch { /* ignore */ }
        try { title.destroy() } catch { /* ignore */ }
        try { dim.destroy() } catch { /* ignore */ }
      }
      const auto = this.time.delayedCall(3500, cleanup)
      dim.setInteractive({ useHandCursor: true })
      const onTap = (): void => {
        try { auto.remove() } catch { /* ignore */ }
        cleanup()
      }
      dim.once('pointerdown', onTap)
      this.input.once('pointerdown', onTap)
    })
  }

  /** Cluster_tumble 级联动画：每一轮 vanish → drop fill */
  private async runCascades(cascades: CascadeStep[], startingTally = 0): Promise<void> {
    // running tally 累加到 winText（Sweet Bonanza 标准：cascade 之间 win 不归零）
    // FS mode 下 startingTally = fsTotalWin（前几次 FS spin 的累积），cascade 在此基础上加
    let runningWin = startingTally
    for (let idx = 0; idx < cascades.length; idx++) {
      const step = cascades[idx]!
      // 1. cluster 高亮 + symbol pop（停留约 620ms 让玩家看清）
      const allCells = step.clusters.flatMap((c) => c.positions)
      this.showCandyTumbleBadge(idx + 1, cascades.length, step.round_win)
      const cleanupHl = this.applyWinHighlights(allCells)
      const cleanupPop = this.popWinningSymbols(allCells)
      const cleanupBurst = this.spawnCoinBurst(allCells)
      this.playSfx(this.sfx('cluster_detected'), 0.55)
      this.time.delayedCall(180, () => this.playSfx(this.sfx('candy_tumble'), 0.5))

      // 2. winText 累加：本 cascade 的 round_win 加入 running tally
      runningWin += step.round_win
      this.showPayoutToast(idx === 0 ? 'TUMBLE WIN' : `TUMBLE ${idx + 1}`, step.round_win)
      this.tweenWinTextTo(runningWin, 500)

      await delay(620)
      cleanupHl()
      cleanupPop()
      cleanupBurst()

      // 3. vanish → drop/refill → next judgment, with a small beat between each phase.
      await delay(110)
      await this.runCascadeStep(step)
      if (idx < cascades.length - 1) await delay(120)
    }
  }

  /**
   * Walking Wilds (Sakura) — 每个 cascade step 是一次 free re-spin：
   *   1. 找到 prevGrid 上的 WILD 位置 → 在新 step.grid_after 上找它们的位置（每个 wild 向左移 1 col，FS 模式 col 0 → col 4 wrap）
   *   2. 滑动 wild 到新位置（500ms slide）
   *   3. 非 wild reels 重转一次（quick spin） + stop on step.grid_after
   *   4. 高亮该步 win lines + tally win
   */
  private async runWalkingWildSteps(result: SpinResponse, startingTally: number): Promise<void> {
    const cascades = result.cascades ?? []
    if (cascades.length === 0) return

    let runningWin = startingTally

    for (const step of cascades) {
      // 1. Banner + SFX：顶部 header 区 slim 横条 slide-in 提示 walking wild 触发的
      //    free re-spin。重新 spin 时 banner 还在屏上 slide-out，节奏不被打断。
      this.playSfx(this.sfx('wild_hit'), 0.45)
      const banner = this.showWalkingWildBanner(step.round)
      const pathCleanup = this.showWalkingWildPath(this.visibleGridSnapshot(), step.grid_after)
      await delay(450)
      pathCleanup()
      // banner 自己 slide-out（260ms），不立刻 destroy；reel 重转时它平行飘出去
      this.time.delayedCall(800, () => banner.destroy())

      // 2. Quick re-spin of non-wild reels then stop on step.grid_after
      this.playSfx(this.sfx('reels_start'), 0.35)
      this.reelGrid.spin()
      await delay(360)
      const stopGap = 110
      for (let c = 0; c < Grid.COLS; c++) {
        this.time.delayedCall(c * stopGap, () => this.playSfx(this.sfx('reel_stop'), 0.4))
      }
      await this.reelGrid.stop(step.grid_after)

      // 4. Highlight winning paylines for this step + tally
      if (step.clusters.length > 0) {
        const cells = step.clusters.flatMap((ln) => ln.positions)
        const cleanupHl = this.applyWinHighlights(cells)
        const cleanupPop = this.popWinningSymbols(cells)
        const cleanupBurst = this.spawnCoinBurst(cells)
        runningWin += step.round_win
        this.showPayoutToast('WILD RE-SPIN WIN', step.round_win)
        this.tweenWinTextTo(runningWin, 500)
        await delay(700)
        cleanupHl()
        cleanupPop()
        cleanupBurst()
      } else {
        await delay(200)
      }
    }
  }

  private visibleGridSnapshot(): SymbolKey[][] {
    const out: SymbolKey[][] = []
    for (let c = 0; c < Grid.COLS; c++) {
      out.push(this.reelGrid.getVisibleColumn(c).map((sym) => sym.getSymbol()))
    }
    return out
  }

  private showWalkingWildPath(prevGrid: SymbolKey[][], nextGrid: SymbolKey[][]): () => void {
    const cfg = GameContext.config()
    if (cfg.id !== 'sakura') return () => undefined

    const wilds: Array<{ from: [number, number]; to: [number, number] }> = []
    for (let c = 0; c < Grid.COLS; c++) {
      for (let r = 0; r < Grid.ROWS; r++) {
        if (prevGrid[c]?.[r] !== 'WILD') continue
        const targetCol = c > 0 ? c - 1 : Grid.COLS - 1
        if (nextGrid[targetCol]?.[r] === 'WILD') {
          wilds.push({ from: [c, r], to: [targetCol, r] })
        }
      }
    }
    if (wilds.length === 0) return () => undefined

    const layer = this.add.container(0, 0).setDepth(930)
    const wildKey = cfg.symbolToAsset.WILD

    for (const wild of wilds) {
      const [fromC, fromR] = wild.from
      const [toC, toR] = wild.to
      const fromX = Grid.ORIGIN_X + fromC * Grid.CELL_WIDTH + Grid.CELL_WIDTH / 2
      const fromY = Grid.ORIGIN_Y + fromR * Grid.CELL_HEIGHT + Grid.CELL_HEIGHT / 2
      const toX = Grid.ORIGIN_X + toC * Grid.CELL_WIDTH + Grid.CELL_WIDTH / 2
      const toY = Grid.ORIGIN_Y + toR * Grid.CELL_HEIGHT + Grid.CELL_HEIGHT / 2

      const lane = this.add.graphics()
      lane.lineStyle(9, 0xfde047, 0.78)
      lane.lineBetween(fromX, fromY, toX, toY)
      lane.lineStyle(3, 0xffffff, 0.82)
      lane.lineBetween(fromX, fromY, toX, toY)
      lane.fillStyle(0xfde047, 0.82)
      lane.fillTriangle(toX - 15, toY - 13, toX - 15, toY + 13, toX + (toX < fromX ? -24 : 24), toY)
      layer.add(lane)

      const fromRing = this.add.graphics()
      fromRing.lineStyle(8, 0xfde047, 0.95)
      fromRing.strokeRoundedRect(
        fromX - Grid.CELL_WIDTH * 0.42,
        fromY - Grid.CELL_HEIGHT * 0.42,
        Grid.CELL_WIDTH * 0.84,
        Grid.CELL_HEIGHT * 0.84,
        24,
      )
      layer.add(fromRing)

      const toRing = this.add.graphics()
      toRing.lineStyle(6, 0xf9a8d4, 0.88)
      toRing.strokeRoundedRect(
        toX - Grid.CELL_WIDTH * 0.38,
        toY - Grid.CELL_HEIGHT * 0.38,
        Grid.CELL_WIDTH * 0.76,
        Grid.CELL_HEIGHT * 0.76,
        22,
      )
      layer.add(toRing)

      if (this.textures.exists(wildKey)) {
        const ghost = this.add.image(fromX, fromY, wildKey).setOrigin(0.5).setAlpha(0.86)
          .setBlendMode(Phaser.BlendModes.ADD)
        const tex = ghost.texture.getSourceImage()
        ghost.setScale(Math.min((Grid.CELL_WIDTH * 0.72) / tex.width, (Grid.CELL_HEIGHT * 0.72) / tex.height))
        layer.add(ghost)
        this.tweens.add({
          targets: ghost,
          x: toX,
          y: toY,
          alpha: 0.36,
          duration: 420,
          ease: 'Cubic.easeOut',
        })
      }
    }

    this.tweens.add({
      targets: layer,
      alpha: 0,
      duration: 180,
      delay: 360,
      ease: 'Sine.easeIn',
    })
    return () => {
      this.tweens.killTweensOf(layer)
      layer.destroy(true)
    }
  }

  private showPayoutToast(label: string, amount: number): void {
    if (amount <= 0) return
    const isTumble = label.startsWith('TUMBLE')
    const cx = Layout.CANVAS_W / 2
    const y = Math.min(Grid.ORIGIN_Y + GridSize.HEIGHT + 64, 1350)
    const container = this.add.container(cx, y + 18).setDepth(980).setAlpha(0)

    const w = isTumble ? 500 : 420
    const h = isTumble ? 78 : 66
    const bg = this.add.graphics()
    bg.fillStyle(isTumble ? 0x300638 : 0x17051f, isTumble ? 0.88 : 0.78)
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 26)
    bg.lineStyle(isTumble ? 4 : 3, isTumble ? 0xfde047 : 0xf9a8d4, isTumble ? 0.82 : 0.58)
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 26)
    bg.lineStyle(2, 0xffffff, isTumble ? 0.36 : 0.22)
    bg.strokeRoundedRect(-w / 2 + 10, -h / 2 + 10, w - 20, h - 20, 19)
    container.add(bg)

    const title = this.add.text(-w / 2 + 34, 0, label, {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: isTumble ? '24px' : '20px',
      color: isTumble ? '#fff7c8' : '#fce7f3',
      stroke: '#2d0a3a',
      strokeThickness: 4,
    }).setOrigin(0, 0.5)
    container.add(title)

    const value = this.add.text(w / 2 - 34, 0, `+${fmt(amount)}`, {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: isTumble ? '34px' : '29px',
      color: isTumble ? '#ffd166' : '#fff7c8',
      stroke: '#5b1a8e',
      strokeThickness: 6,
      shadow: { offsetX: 0, offsetY: 3, color: '#000', blur: 5, fill: true },
    }).setOrigin(1, 0.5)
    fitTextToWidth(value, 230)
    container.add(value)

    this.tweens.add({
      targets: container,
      y,
      alpha: 1,
      duration: 180,
      ease: 'Cubic.easeOut',
    })
    this.tweens.add({
      targets: container,
      y: y - 18,
      alpha: 0,
      delay: 980,
      duration: 260,
      ease: 'Sine.easeIn',
      onComplete: () => container.destroy(true),
    })
  }

  /**
   * Walking Wild "FREE RE-SPIN" banner — 放在 reel zone 上方的空区域，
   * **从上方下落弹出**，**透明融入紫红色 sakura 背景**，不要硬底板。
   *   位置: y = Grid.ORIGIN_Y - 95（reel zone 顶上方 ~95px）
   *   尺寸: 自然，不超 600px 宽
   *   动画: 从 -40 偏移落入 + alpha 0→1（240ms），hold 200ms，alpha→0（240ms）
   *   底板: 仅 win_flash 极低 alpha 做光晕，无硬框
   */
  private showWalkingWildBanner(stepIdx: number): Phaser.GameObjects.Container {
    const cfgFs = GameContext.config()
    const palette = cfgFs.themeAssets?.palette
    const cx = Layout.CANVAS_W / 2
    const targetY = Math.max(330, Grid.ORIGIN_Y - 165)
    const fromY = targetY - 36

    const container = this.add.container(cx, fromY).setDepth(900).setAlpha(0)

    // 极淡光晕（融背景）— 用 win_flash 或 celebration_burst，alpha 0.22 防硬边
    const glowKey = cfgFs.themeAssets?.winFlashKey ?? cfgFs.themeAssets?.celebrationBurstKey
      ?? 'sakura_fx_celebration_burst'
    if (glowKey && this.textures.exists(glowKey)) {
      const glow = this.add.image(0, 0, glowKey).setOrigin(0.5).setAlpha(0.22)
        .setBlendMode(Phaser.BlendModes.ADD)
      const gtex = glow.texture.getSourceImage()
      const gscale = Math.min(640 / gtex.width, 160 / gtex.height)
      glow.setScale(gscale * 1.4, gscale * 0.9)
      container.add(glow)
    }

    // WILD 图标（左侧），柔脉冲一次
    const wildKey = cfgFs.symbolToAsset?.WILD ?? 'sakura_sym_omamori_charm'
    if (this.textures.exists(wildKey)) {
      const wild = this.add.image(-220, 0, wildKey).setOrigin(0.5).setScale(0.30)
      container.add(wild)
      this.tweens.add({
        targets: wild,
        scale: 0.34,
        duration: 380,
        yoyo: true,
        repeat: 0,
        ease: 'Sine.easeOut',
      })
    }

    // 文字（描边压住背景；无底板，靠描边读出）
    const goldHex = palette ? Phaser.Display.Color.IntegerToColor(palette.gold).rgba : '#fde047'
    const strokeHex = palette ? palette.txtStroke : '#5b1a8e'

    const title = this.add.text(-130, -18, 'WALKING WILD', {
      fontFamily: 'Arial Black, sans-serif',
      fontSize: '34px',
      color: goldHex,
      stroke: strokeHex,
      strokeThickness: 6,
      shadow: { offsetX: 0, offsetY: 2, color: '#000', blur: 4, fill: true },
    }).setOrigin(0, 0.5)
    container.add(title)

    const subtitle = this.add.text(-130, 22, `FREE RE-SPIN  ${stepIdx} / 3`, {
      fontFamily: 'Arial Black, sans-serif',
      fontSize: '26px',
      color: '#ffffff',
      stroke: strokeHex,
      strokeThickness: 5,
      shadow: { offsetX: 0, offsetY: 2, color: '#000', blur: 3, fill: true },
    }).setOrigin(0, 0.5)
    container.add(subtitle)

    // Pop-in：从上方下落 + 淡入
    this.tweens.add({
      targets: container,
      y: targetY,
      alpha: 1,
      duration: 240,
      ease: 'Cubic.easeOut',
    })
    // Hold 200ms 后淡出
    this.time.delayedCall(440, () => {
      this.tweens.add({
        targets: container,
        alpha: 0,
        y: targetY - 14,
        duration: 240,
        ease: 'Sine.easeIn',
      })
    })

    return container
  }

  /** Tween winText 从当前值到 target，duration ms。 */
  private tweenWinTextTo(target: number, duration: number): void {
    const current = parseInt(this.winText.text.replace(/\D/g, ''), 10) || 0
    const counter = { v: current }
    this.tweens.killTweensOf(counter)
    this.tweens.add({
      targets: counter,
      v: target,
      duration,
      ease: 'Cubic.easeOut',
      onUpdate: () => {
        this.winText.setText(fmt(Math.floor(counter.v)))
        fitTextToWidth(this.winText, 130)
      },
      onComplete: () => {
        this.winText.setText(fmt(target))
        fitTextToWidth(this.winText, 130)
      },
    })
  }

  /** 单步：消除指定 cluster 格 + 上方下落 + 顶部新符号填补 */
  private async runCascadeStep(step: CascadeStep): Promise<void> {
    const clearedByCol: Set<number>[] = Array.from({ length: Grid.COLS }, () => new Set())
    for (const cl of step.clusters) {
      for (const [c, r] of cl.positions) clearedByCol[c]!.add(r)
    }

    // Phase 1: vanish (fade + shrink) — 并行
    const vanishTweens: Promise<void>[] = []
    for (let c = 0; c < Grid.COLS; c++) {
      for (const r of clearedByCol[c]!) {
        const sym = this.reelGrid.getSymbolObjectAt(c, r)
        if (!sym) continue
        const baseScale = sym.scale
        vanishTweens.push(
          new Promise<void>((resolve) => {
            this.tweens.add({
              targets: sym,
              alpha: 0,
              scale: baseScale * 0.4,
              duration: 240,
              ease: 'Cubic.easeIn',
              onComplete: () => resolve(),
            })
          }),
        )
      }
    }
    await Promise.all(vanishTweens)

    // Phase 2: drop + fill — surviving 实例下移到底部行，cleared 实例改纹理变成顶部新符号下落
    const dropTweens: Promise<void>[] = []
    const cellY = (r: number) =>
      Grid.ORIGIN_Y + r * Grid.CELL_HEIGHT + Grid.CELL_HEIGHT / 2

    for (let c = 0; c < Grid.COLS; c++) {
      const colVisible = this.reelGrid.getVisibleColumn(c)
      if (colVisible.length === 0) continue

      const cleared = clearedByCol[c]!
      const surviving: { sym: typeof colVisible[number]; r_old: number }[] = []
      const free: typeof colVisible[number][] = []
      for (let r = 0; r < Grid.ROWS; r++) {
        const sym = colVisible[r]!
        if (cleared.has(r)) free.push(sym)
        else surviving.push({ sym, r_old: r })
      }

      const k = free.length
      const targetCol: SymbolKey[] = step.grid_after[c] ?? []

      // Surviving → 底部 (ROWS - k) 个位置，从 row k 起，按原顺序
      // 注意：起始行号是 k（消除数），不是 (ROWS - k)；后端 dropAndFill 把
      // 新符号放在 grid_after[c][0..k-1]，surviving 放在 [k..ROWS-1]。
      surviving.forEach((s, i) => {
        const newR = k + i
        const targetY = cellY(newR)
        dropTweens.push(
          new Promise<void>((resolve) => {
            this.tweens.add({
              targets: s.sym,
              y: targetY,
              duration: 320,
              ease: 'Cubic.easeIn',
              onComplete: () => resolve(),
            })
          }),
        )
      })

      // Free 实例 → 顶部 [0..k-1] 行；纹理改为 grid_after 顶 k 个；起始 y 在画面上方
      free.forEach((sym, i) => {
        const newR = i
        const newSym = targetCol[newR]
        if (newSym !== undefined) sym.setSymbol(newSym)
        sym.setAlpha(1)
        const targetY = cellY(newR)
        sym.y = targetY - Grid.CELL_HEIGHT * (k + 1)
        dropTweens.push(
          new Promise<void>((resolve) => {
            this.tweens.add({
              targets: sym,
              y: targetY,
              duration: 380 + i * 40,
              ease: 'Bounce.easeOut',
              onComplete: () => resolve(),
            })
          }),
        )
      })
    }
    await Promise.all(dropTweens)
  }

  private startSpinRotation(): void {
    this.spinTween?.stop()
    this.stopSpinIdleMotion()
    this.spinBtn.setScale(this.spinBtnBaseScale * 1.035)
    this.spinBtn.setAlpha(1)
    this.spinBtn.setAngle(0)
    this.spinTween = this.tweens.add({
      targets: this.spinBtn,
      angle: 360,
      duration: this.turboOn ? 360 : 620,
      repeat: -1,
      ease: 'Linear',
    })
  }

  private stopSpinRotation(): void {
    this.spinTween?.stop()
    this.spinTween = null
    this.tweens.add({
      targets: this.spinBtn,
      scale: this.spinBtnBaseScale,
      angle: 0,
      duration: 200,
      ease: 'Cubic.easeOut',
      onComplete: () => this.startSpinIdleMotion(),
    })
  }

  private startSpinIdleMotion(): void {
    if (!this.spinBtn || this.spinState !== 'idle') return
    if (this.thermalQuiet) return
    this.spinIdleTween?.stop()
    this.spinIdlePulseTween?.stop()
    this.tweens.killTweensOf(this.spinBtn)
    this.spinBtn.setAngle(0)
    this.spinBtn.setAlpha(1)
    this.spinBtn.setScale(this.spinBtnBaseScale)
    this.spinIdleTween = this.tweens.add({
      targets: this.spinBtn,
      alpha: 0.9,
      duration: 1100,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    })
    this.spinIdlePulseTween = this.tweens.add({
      targets: this.spinBtn,
      scale: this.spinBtnBaseScale * 1.035,
      duration: 950,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    })
  }

  private stopSpinIdleMotion(): void {
    this.spinIdleTween?.stop()
    this.spinIdleTween = null
    this.spinIdlePulseTween?.stop()
    this.spinIdlePulseTween = null
    if (this.spinBtn) {
      this.spinBtn.setAngle(0)
      this.spinBtn.setAlpha(1)
    }
  }

  private changeBet(delta: number): void {
    if (this.spinState !== 'idle') return
    // Sakura: 不允许低于全局 10K 最小消耗档位
    let lowerBound = 0
    if (GameContext.gameId() === 'sakura') {
      const idx = BET_LEVELS.findIndex((b) => b >= 10_000)
      if (idx >= 0) lowerBound = idx
    }
    const next = Phaser.Math.Clamp(this.betIndex + delta, lowerBound, BET_LEVELS.length - 1)
    if (next === this.betIndex) return
    this.betIndex = next
    this.bet = BET_LEVELS[next]!
    this.betText.setText(fmt(this.bet))
    fitTextToWidth(this.betText, 220)
    this.playSfx(this.sfx('spin_tap'), 0.4)
  }

  // ─── 副栏行为（按钮已删除，方法保留留作 Lobby 后续 wire-up） ──
  // toggleAutoSpin / toggleTurbo / onMaxBetTap / onBuyBonusTap 已删除
  // 后续 Lobby 弹窗里需要时再恢复

  private goToGameLobby(): void {
    const href = `/play/${GameContext.gameId()}`
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'sss-nav', href }, '*')
        return
      }
    } catch {
      // Cross-origin guard: fall through to direct navigation.
    }
    window.location.href = `${window.location.protocol}//${window.location.hostname}:3000${href}`
  }

  private showInfoPanel(): void {
    const gameId = GameContext.gameId()
    const lines = gameId === 'sakura'
      ? [
          'Walking Wilds',
          'Wild symbols can move left after a winning spin.',
          'Scatter coins open Free Spins.',
          '30 paylines. Minimum bet starts at 10K.',
        ]
      : [
          'Cluster Pays',
          'Match 5+ touching candies to win.',
          'Winning candies tumble until the board settles.',
          'Scatter lollipops can open Free Spins.',
        ]
    this.showModal(gameId === 'sakura' ? 'SAKURA RULES' : 'CANDY RULES', lines)
  }

  private showSettingsPanel(): void {
    this.showModal('SETTINGS', [
      `Sound: ${this.sound.mute ? 'OFF' : 'ON'}`,
      `Turbo Spin: ${this.turboOn ? 'ON' : 'OFF'}`,
      'Tap a switch below to change it.',
    ], [
      {
        label: this.sound.mute ? 'SOUND ON' : 'SOUND OFF',
        onTap: () => {
          this.sound.mute = !this.sound.mute
          this.showSettingsPanel()
        },
      },
      {
        label: this.turboOn ? 'NORMAL' : 'TURBO',
        onTap: () => {
          this.turboOn = !this.turboOn
          setSpinPreset(this.turboOn ? 'turbo' : 'normal')
          this.showSettingsPanel()
        },
      },
    ])
  }

  private showModal(
    title: string,
    lines: readonly string[],
    actions: readonly { label: string; onTap: () => void }[] = [],
  ): void {
    this.activeModal?.destroy(true)
    const { CANVAS_W, CANVAS_H } = Layout
    const modal = this.add.container(0, 0).setDepth(5000)
    const dim = this.add.rectangle(CANVAS_W / 2, CANVAS_H / 2, CANVAS_W, CANVAS_H, 0x030008, 0.64)
      .setInteractive({ useHandCursor: false })
    modal.add(dim)

    const panelW = 850
    const panelH = actions.length > 0 ? 620 : 520
    const panelX = (CANVAS_W - panelW) / 2
    const panelY = (CANVAS_H - panelH) / 2
    const g = this.add.graphics()
    g.fillStyle(0x17051f, 0.98)
    g.fillRoundedRect(panelX, panelY, panelW, panelH, 42)
    g.lineStyle(8, 0xffd66b, 0.92)
    g.strokeRoundedRect(panelX, panelY, panelW, panelH, 42)
    g.lineStyle(3, 0xff6eb7, 0.72)
    g.strokeRoundedRect(panelX + 16, panelY + 16, panelW - 32, panelH - 32, 32)
    modal.add(g)

    modal.add(this.add.text(CANVAS_W / 2, panelY + 82, title, {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: '48px',
      color: '#fff7c8',
      stroke: '#5b1a8e',
      strokeThickness: 7,
      align: 'center',
    }).setOrigin(0.5))

    lines.forEach((line, i) => {
      const isLead = i === 0
      modal.add(this.add.text(CANVAS_W / 2, panelY + 168 + i * 62, line, {
        fontFamily: isLead ? 'Arial Black, Impact, sans-serif' : 'Arial, sans-serif',
        fontSize: isLead ? '32px' : '28px',
        color: isLead ? '#ffffff' : '#fce7f3',
        stroke: '#2d0a3a',
        strokeThickness: isLead ? 5 : 3,
        align: 'center',
        wordWrap: { width: panelW - 110 },
      }).setOrigin(0.5))
    })

    actions.forEach((action, i) => {
      const buttonW = 270
      const buttonH = 82
      const totalW = actions.length * buttonW + (actions.length - 1) * 28
      const x = CANVAS_W / 2 - totalW / 2 + i * (buttonW + 28)
      const y = panelY + panelH - 166
      const btn = this.add.container(x, y)
      const bg = this.add.graphics()
      bg.fillStyle(0xec4899, 0.95)
      bg.fillRoundedRect(0, 0, buttonW, buttonH, 24)
      bg.lineStyle(4, 0xffd66b, 0.95)
      bg.strokeRoundedRect(0, 0, buttonW, buttonH, 24)
      const label = this.add.text(buttonW / 2, buttonH / 2, action.label, {
        fontFamily: 'Arial Black, Impact, sans-serif',
        fontSize: '26px',
        color: '#ffffff',
        stroke: '#5b1a8e',
        strokeThickness: 4,
      }).setOrigin(0.5)
      btn.add([bg, label])
      btn.setSize(buttonW, buttonH)
      btn.setInteractive(
        new Phaser.Geom.Rectangle(0, 0, buttonW, buttonH),
        Phaser.Geom.Rectangle.Contains,
      )
      btn.input!.cursor = 'pointer'
      btn.on('pointerdown', () => btn.setScale(0.97))
      btn.on('pointerup', () => {
        btn.setScale(1)
        action.onTap()
      })
      btn.on('pointerout', () => btn.setScale(1))
      modal.add(btn)
      const hit = this.add.zone(x + buttonW / 2, y + buttonH / 2, buttonW, buttonH)
        .setInteractive({ useHandCursor: true })
        .on('pointerdown', () => btn.setScale(0.97))
        .on('pointerup', () => {
          btn.setScale(1)
          action.onTap()
        })
        .on('pointerout', () => btn.setScale(1))
      modal.add(hit)
    })

    const closeY = panelY + panelH - 64
    const close = this.add.text(CANVAS_W / 2, closeY, 'OK', {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: '34px',
      color: '#fff7c8',
      stroke: '#5b1a8e',
      strokeThickness: 5,
    }).setOrigin(0.5)
      .setPadding(38, 14, 38, 14)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => {
        modal.destroy(true)
        if (this.activeModal === modal) this.activeModal = null
      })
    modal.add(close)
    this.activeModal = modal
  }

  private makeRoundIconButton(
    x: number,
    y: number,
    size: number,
    label: string,
    onTap: () => void,
  ): void {
    const g = this.add.graphics()
    const r = size / 2
    g.fillStyle(0xff6eb7, 0.98)
    g.fillCircle(r, r, r)
    g.lineStyle(6, 0xffe47a, 1)
    g.strokeCircle(r, r, r - 3)
    g.lineStyle(3, 0xffffff, 0.86)
    g.strokeCircle(r, r, r - 11)

    const key = `top_icon_${label}_${size}`
    g.lineStyle(8, 0xffffff, 1)
    if (label === '<') {
      g.lineBetween(r + 12, r - 18, r - 10, r)
      g.lineBetween(r - 10, r, r + 12, r + 18)
    } else if (label === 'O') {
      g.strokeCircle(r, r, 12)
      for (let i = 0; i < 8; i++) {
        const a = (Math.PI * 2 * i) / 8
        const x1 = r + Math.cos(a) * 20
        const y1 = r + Math.sin(a) * 20
        const x2 = r + Math.cos(a) * 27
        const y2 = r + Math.sin(a) * 27
        g.lineBetween(x1, y1, x2, y2)
      }
    } else if (label === 'i') {
      g.fillStyle(0xffffff, 1)
      g.fillCircle(r, r - 18, 5)
      g.fillRoundedRect(r - 5, r - 6, 10, 30, 5)
    } else {
      g.lineBetween(r - 16, r - 16, r + 16, r + 16)
      g.lineBetween(r + 16, r - 16, r - 16, r + 16)
    }

    if (this.textures.exists(key)) {
      this.textures.remove(key)
    }
    g.generateTexture(key, size, size)
    g.destroy()

    const group = this.add.container(x, y).setDepth(21)
    const base = this.add.image(0, 0, key).setOrigin(0.5)
    group.add(base)
    group.setSize(size, size)
    group.setInteractive(
      new Phaser.Geom.Circle(0, 0, r),
      Phaser.Geom.Circle.Contains,
    )
    group.input!.cursor = 'pointer'
    group.on('pointerdown', () => group.setScale(0.94))
    group.on('pointerup', () => {
      group.setScale(1)
      onTap()
    })
    group.on('pointerout', () => group.setScale(1))
    this.add.zone(x, y, size, size)
      .setOrigin(0.5)
      .setDepth(22)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => group.setScale(0.94))
      .on('pointerup', () => {
        group.setScale(1)
        onTap()
      })
      .on('pointerout', () => group.setScale(1))
  }

  // ─── Win 表现：高亮 / 粒子 / 计数器 / 弹窗 ───────────

  /** 中奖格上叠 NC-098 高亮框 + 脉冲 tween；返回 cleanup 函数 */
  private applyWinHighlights(cells: [number, number][]): () => void {
    const objs: Phaser.GameObjects.Graphics[] = []
    for (const [col, row] of cells) {
      const cx = Grid.ORIGIN_X + col * Grid.CELL_WIDTH + Grid.CELL_WIDTH / 2
      const cy = Grid.ORIGIN_Y + row * Grid.CELL_HEIGHT + Grid.CELL_HEIGHT / 2
      const w = Grid.CELL_WIDTH * 0.92
      const h = Grid.CELL_HEIGHT * 0.92
      const hl = this.add.graphics().setPosition(cx, cy).setDepth(950)
      hl.lineStyle(12, 0xff4fb5, 0.58)
      hl.strokeRoundedRect(-w / 2, -h / 2, w, h, 24)
      hl.lineStyle(5, 0xffde68, 0.95)
      hl.strokeRoundedRect(-w / 2, -h / 2, w, h, 24)
      hl.lineStyle(2, 0xffffff, 0.45)
      hl.strokeRoundedRect(-w / 2 + 10, -h / 2 + 10, w - 20, h - 20, 18)
      hl.setAlpha(1)
      const baseScale = hl.scaleX
      this.tweens.add({
        targets: hl,
        scale: baseScale * 1.12,
        alpha: 0.72,
        yoyo: true,
        repeat: -1,
        duration: 380,
        ease: 'Sine.inOut',
      })
      objs.push(hl)
    }
    return () => {
      for (const o of objs) {
        this.tweens.killTweensOf(o)
        o.destroy()
      }
    }
  }

  /** 中奖格喷金币粒子；返回 cleanup */
  private spawnCoinBurst(cells: [number, number][]): () => void {
    const cfg = GameContext.config()
    const sparkleKey = cfg.themeAssets?.sparkleParticleKey && this.textures.exists(cfg.themeAssets.sparkleParticleKey)
      ? cfg.themeAssets.sparkleParticleKey
      : AssetKeys.fx.sparkles
    const records: {
      emitter: Phaser.GameObjects.Particles.ParticleEmitter
      stopTimer: Phaser.Time.TimerEvent
      destroyTimer: Phaser.Time.TimerEvent
    }[] = []
    for (const [col, row] of cells) {
      const cx = Grid.ORIGIN_X + col * Grid.CELL_WIDTH + Grid.CELL_WIDTH / 2
      const cy = Grid.ORIGIN_Y + row * Grid.CELL_HEIGHT + Grid.CELL_HEIGHT / 2
      const emitter = this.add.particles(cx, cy, sparkleKey, {
        speed: { min: 120, max: 320 },
        angle: { min: 200, max: 340 },
        scale: { start: 0.18, end: 0.04 },
        alpha: { start: 1, end: 0 },
        lifespan: 620,
        gravityY: 700,
        frequency: 80,
        blendMode: 'ADD',
        quantity: 1,
      })
      emitter.setDepth(1000)
      const stopTimer = this.time.delayedCall(520, () => emitter.stop())
      const destroyTimer = this.time.delayedCall(1500, () => {
        try {
          emitter.destroy()
        } catch {
          /* ok */
        }
      })
      records.push({ emitter, stopTimer, destroyTimer })
    }
    return () => {
      for (const r of records) {
        r.stopTimer.remove()
        r.destroyTimer.remove()
        try {
          r.emitter.stop()
          r.emitter.destroy()
        } catch {
          /* ok */
        }
      }
    }
  }

  /** 中奖符号脉冲缩放（yoyo 循环），cleanup 时还原 */
  private popWinningSymbols(cells: [number, number][]): () => void {
    const records: { sym: Phaser.GameObjects.Image; baseScale: number }[] = []
    const seen = new Set<string>()
    for (const [col, row] of cells) {
      const key = `${col},${row}`
      if (seen.has(key)) continue
      seen.add(key)
      const sym = this.reelGrid.getSymbolObjectAt(col, row)
      if (!sym) continue
      const baseScale = sym.scale
      this.tweens.add({
        targets: sym,
        scale: baseScale * 1.18,
        yoyo: true,
        repeat: -1,
        duration: 360,
        ease: 'Sine.inOut',
      })
      records.push({ sym, baseScale })
    }
    return () => {
      for (const r of records) {
        this.tweens.killTweensOf(r.sym)
        r.sym.setScale(r.baseScale)
      }
    }
  }

  /** WIN 数字从 0 滚到目标 */
  private animateWinCounter(target: number, duration: number): void {
    const counter = { v: 0 }
    this.tweens.add({
      targets: counter,
      v: target,
      duration,
      ease: 'Cubic.easeOut',
      onUpdate: () => {
        this.winText.setText(fmt(Math.floor(counter.v)))
        fitTextToWidth(this.winText, 130)
      },
      onComplete: () => {
        this.winText.setText(fmt(target))
        fitTextToWidth(this.winText, 130)
      },
    })
  }

  /** 中奖弹窗（big/mega/super/jackpot）。可点击或自动关闭。 */
  // ─── Big Win 2-3s 全屏金色庆祝（ADR-035 #3）──────────
  private showWinPopup(
    level: 'big' | 'mega' | 'super' | 'jackpot',
    amount: number,
  ): Promise<void> {
    const cfgWin = GameContext.config()
    if (cfgWin.themeAssets?.bigWinPanelKey) {
      return this.showWinPopupThemed(level, amount)
    }
    const badgeKey = (
      {
        big: AssetKeys.win.gen_badge_big,
        mega: AssetKeys.win.gen_badge_mega,
        super: AssetKeys.win.gen_badge_super,
        jackpot: AssetKeys.win.gen_badge_jackpot,
      } as const
    )[level]
    const bgKey = level === 'jackpot'
      ? AssetKeys.overlay.jackpot_generated
      : AssetKeys.overlay.win_celebration
    if (GameContext.gameId() === 'candy' && (!this.textures.exists(badgeKey) || !this.textures.exists(bgKey))) {
      return this.loadLazyAssets('win', CANDY_LAZY_WIN_ASSETS, CANDY_LAZY_WIN_AUDIO)
        .then(() => this.showWinPopup(level, amount))
    }
    // Win-tier stinger — plays once at popup entry. Was previously missing
    // for super/jackpot (they shared sfx_big_win_count with big/mega), so
    // visuals were tier-correct but audio was indistinguishable.
    this.playWinTierStinger(level)
    return new Promise((resolve) => {
      const { CANVAS_W, CANVAS_H } = Layout
      const cx = CANVAS_W / 2
      const cy = CANVAS_H / 2

      // 全屏暗化
      const dim = this.add
        .rectangle(cx, cy, CANVAS_W, CANVAS_H, 0x05000a, 0.78)
        .setOrigin(0.5)
        .setDepth(2000)

      // Generated full-screen celebration art. Text/amount stays dynamic in Phaser.
      const hdBg = this.add
        .image(
          cx,
          cy,
          bgKey,
        )
        .setOrigin(0.5)
        .setDepth(2001)
      const hdTex = hdBg.texture.getSourceImage()
      const bgScale = Math.max(CANVAS_W / hdTex.width, CANVAS_H / hdTex.height)
      hdBg.setScale(bgScale).setAlpha(0)
      this.tweens.add({
        targets: hdBg,
        alpha: 0.85,
        duration: 360,
        ease: 'Sine.easeOut',
      })

      const glow = this.add.graphics().setDepth(2002).setAlpha(0)
      glow.fillStyle(level === 'jackpot' ? 0xfff1a8 : 0xff71d8, 0.28)
      glow.fillCircle(cx, cy - 70, 360)
      glow.fillStyle(0xffffff, 0.16)
      glow.fillCircle(cx, cy - 70, 250)
      this.tweens.add({
        targets: glow,
        alpha: 1,
        scale: 1.08,
        duration: 780,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.inOut',
      })

      const badgeW = level === 'jackpot' ? CANVAS_W * 0.88 : CANVAS_W * 0.82
      const badgeH = level === 'jackpot' ? 540 : 560
      const amountPanel = this.add.container(cx, cy - 100).setDepth(2003).setAlpha(0).setScale(0.62)
      const badge = this.add.image(0, 0, badgeKey).setOrigin(0.5)
      fitTo(badge, badgeW, badgeH)
      const amountY = level === 'jackpot' ? 118 : 122

      const amountStyle: Phaser.Types.GameObjects.Text.TextStyle = {
        fontFamily: 'Arial Black, Impact, sans-serif',
        fontSize: level === 'jackpot' ? '88px' : '78px',
        color: '#fff7c8',
        stroke: '#5b1a8e',
        strokeThickness: 10,
        align: 'center',
        shadow: { offsetX: 0, offsetY: 6, color: '#000', blur: 8, fill: true },
      }
      const amountTxt = this.add
        .text(0, amountY, '$0', amountStyle)
        .setOrigin(0.5)
      amountPanel.add([badge, amountTxt])
      this.tweens.add({
        targets: amountPanel,
        scale: 1,
        alpha: 1,
        duration: 380,
        delay: 180,
        ease: 'Back.easeOut',
      })

      // 金额从 0 滚到 target — 持续时间随 level 拉长
      const countDur =
        level === 'jackpot' ? 2400 : level === 'super' ? 1800 : level === 'mega' ? 1400 : 1100
      const countTimer = this.time.delayedCall(380, () => {
        const counter = { v: 0 }
        this.tweens.add({
          targets: counter,
          v: amount,
          duration: countDur,
          ease: 'Cubic.easeOut',
          onUpdate: () => {
            amountTxt.setText('$' + fmt(Math.floor(counter.v)))
            fitTextToWidth(amountTxt, badgeW * 0.72)
          },
          onComplete: () => {
            amountTxt.setText('$' + fmt(amount))
            fitTextToWidth(amountTxt, badgeW * 0.72)
            // 数字落定时一个轻微 punch 缩放
            this.tweens.add({
              targets: amountTxt,
              scale: 1.08,
              yoyo: true,
              duration: 220,
              ease: 'Cubic.easeOut',
            })
          },
        })
        // 数字滚动期播放 SFX
        this.playSfx(this.sfx('big_win_count'), 0.7)
      })

      // 金币雨 — 顶部喷出，下落
      const coinRain = this.add.particles(cx, -40, AssetKeys.win.gold_coins, {
        x: { min: 60, max: CANVAS_W - 60 },
        y: -40,
        speedY: { min: 320, max: 520 },
        speedX: { min: -60, max: 60 },
        scale: { start: 0.18, end: 0.06 },
        alpha: { start: 1, end: 0.4 },
        lifespan: 2400,
        frequency: 80,
        quantity: 2,
        rotate: { min: 0, max: 360 },
        gravityY: 240,
      })
      coinRain.setDepth(2002)

      let cleaned = false
      const cleanup = (): void => {
        if (cleaned) return
        cleaned = true
        // Resolve FIRST so even if a destroy() below throws, the await chain
        // unblocks and the user gets back to the canvas. Previously a single
        // throw inside cleanup left `resolve()` unreachable → tap-to-dismiss
        // hung forever (auto-dismiss path was identical but apparently
        // never raced with mid-tween destroys).
        try { resolve() } catch { /* ignore */ }
        try { countTimer.remove() } catch { /* ignore */ }
        try { coinRain.stop(); coinRain.destroy() } catch { /* ignore */ }
        try { this.tweens.killTweensOf(glow) } catch { /* ignore */ }
        try { this.tweens.killTweensOf(amountPanel) } catch { /* ignore */ }
        try { this.tweens.killTweensOf(amountTxt) } catch { /* ignore */ }
        try { this.tweens.killTweensOf(hdBg) } catch { /* ignore */ }
        try { glow.destroy() } catch { /* ignore */ }
        try { amountPanel.destroy() } catch { /* ignore */ }
        try { amountTxt.destroy() } catch { /* ignore */ }
        try { hdBg.destroy() } catch { /* ignore */ }
        try { dim.destroy() } catch { /* ignore */ }
      }

      // 自动 dismiss（jackpot 4s / super 3.4s / mega 3s / big 2.6s — ADR-035 锁定 2-3s）
      const debugHoldMs = new URLSearchParams(window.location.search).has('winTest') ? 9000 : 0
      const holdMs = debugHoldMs || (
        level === 'jackpot' ? 4000 :
        level === 'super'   ? 3400 :
        level === 'mega'    ? 3000 :
                              2600
      )
      const auto = this.time.delayedCall(holdMs, cleanup)
      dim.setInteractive({ useHandCursor: true })
      // Tap to dismiss. Listen on BOTH dim itself AND the scene-level input
      // because iOS Safari occasionally misses pointerdown on a child object
      // when other touch handlers (Phaser autospin postMessage etc.) are
      // active. The scene-level listener is a belt-and-suspenders fallback.
      const onTap = (): void => {
        try { auto.remove() } catch { /* ignore */ }
        cleanup()
      }
      dim.once('pointerdown', onTap)
      this.input.once('pointerdown', onTap)
    })
  }

  // ─── Free Spins event-style 全屏接管（ADR-035 #4）──────
  private showFreeSpinsEvent(spins: number): Promise<void> {
    const cfgFs = GameContext.config()
    if (cfgFs.themeAssets?.fsStartPanelKey) {
      return this.showFreeSpinsEventThemed(spins)
    }
    return new Promise((resolve) => {
      const { CANVAS_W, CANVAS_H } = Layout
      const cx = CANVAS_W / 2
      const cy = CANVAS_H / 2

      const dim = this.add
        .rectangle(cx, cy, CANVAS_W, CANVAS_H, 0x05000a, 0.78)
        .setOrigin(0.5)
        .setDepth(2000)

      const bg = this.add
        .image(cx, cy, AssetKeys.overlay.free_spins_generated)
        .setOrigin(0.5)
        .setDepth(2001)
      const tex = bg.texture.getSourceImage()
      bg.setScale(Math.max(CANVAS_W / tex.width, CANVAS_H / tex.height)).setAlpha(0)
      this.tweens.add({ targets: bg, alpha: 0.88, duration: 420, ease: 'Sine.easeOut' })

      const glow = this.add.graphics().setDepth(2002).setAlpha(0)
      glow.fillStyle(0xfff1a8, 0.24)
      glow.fillCircle(cx, cy - 50, 370)
      glow.fillStyle(0xff74e8, 0.12)
      glow.fillCircle(cx, cy - 50, 470)
      this.tweens.add({
        targets: glow,
        scale: 1.08,
        alpha: 1,
        duration: 950,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.inOut',
      })

      const title = this.add.image(cx, cy - 260, AssetKeys.win.gen_title_free_spins).setOrigin(0.5).setDepth(2004)
      fitTo(title, CANVAS_W * 0.88, 330)
      const titleScale = title.scale
      title.setScale(titleScale * 0.25).setAlpha(0)
      this.tweens.add({
        targets: title,
        scale: titleScale,
        alpha: 1,
        duration: 540,
        ease: 'Back.easeOut',
      })

      const panelW = CANVAS_W * 0.76
      const panelH = 300
      const panel = this.add.container(cx, cy + 75).setDepth(2003).setScale(0.55).setAlpha(0)
      const panelFrame = this.add.image(0, 0, AssetKeys.win.gen_fs_count_frame).setOrigin(0.5)
      panelFrame.setDisplaySize(panelW, panelH)

      const numTxt = this.add
        .text(0, -48, `${spins}`, {
          fontFamily: 'Arial Black, Impact, sans-serif',
          fontSize: '150px',
          color: '#fff7c8',
          stroke: '#5b1a8e',
          strokeThickness: 12,
          shadow: { offsetX: 0, offsetY: 6, color: '#000', blur: 8, fill: true },
        })
        .setOrigin(0.5)
      fitTextToWidth(numTxt, panelW - 100)
      const labelTxt = this.add
        .text(0, 78, 'FREE SPINS AWARDED', {
          fontFamily: 'Arial Black, Impact, sans-serif',
          fontSize: '42px',
          color: '#ffffff',
          stroke: '#5b1a8e',
          strokeThickness: 6,
        })
        .setOrigin(0.5)
      fitTextToWidth(labelTxt, panelW - 110)
      panel.add([panelFrame, numTxt, labelTxt])
      this.tweens.add({
        targets: panel,
        scale: 1,
        alpha: 1,
        duration: 440,
        delay: 260,
        ease: 'Back.easeOut',
      })

      const drops = this.add.particles(cx, -60, AssetKeys.fx.sparkles, {
        x: { min: 0, max: CANVAS_W },
        y: -60,
        speedY: { min: 180, max: 360 },
        speedX: { min: -80, max: 80 },
        scale: { start: 0.26, end: 0.06 },
        alpha: { start: 1, end: 0 },
        lifespan: 2200,
        frequency: 70,
        quantity: 3,
        blendMode: 'ADD',
      })
      drops.setDepth(2002)

      const okBtn = this.add.container(cx, CANVAS_H - 310).setDepth(2005).setAlpha(0)
      const okBg = this.add.graphics()
      okBg.fillStyle(0xff4fb5, 0.94)
      okBg.fillRoundedRect(-118, -45, 236, 90, 45)
      okBg.lineStyle(6, 0xffe66d, 1)
      okBg.strokeRoundedRect(-118, -45, 236, 90, 45)
      const okText = this.add
        .text(0, -1, 'OK', {
          fontFamily: 'Arial Black, Impact, sans-serif',
          fontSize: '42px',
          color: '#ffffff',
          stroke: '#6b145b',
          strokeThickness: 6,
        })
        .setOrigin(0.5)
      okBtn.add([okBg, okText])
      this.tweens.add({ targets: okBtn, alpha: 1, duration: 320, delay: 900 })
      okBtn.setSize(236, 90)
      okBtn.setInteractive({ useHandCursor: true })

      this.playSfx(this.sfx('free_spins_trigger'), 0.65)
      this.time.delayedCall(640, () =>
        this.playSfx(this.sfx('free_spin_start'), 0.55),
      )

      let cleaned = false
      const cleanup = (): void => {
        if (cleaned) return
        cleaned = true
        try { resolve() } catch { /* ignore */ }
        try { drops.stop(); drops.destroy() } catch { /* ignore */ }
        try { this.tweens.killTweensOf(bg) } catch { /* ignore */ }
        try { this.tweens.killTweensOf(glow) } catch { /* ignore */ }
        try { this.tweens.killTweensOf(title) } catch { /* ignore */ }
        try { this.tweens.killTweensOf(panel) } catch { /* ignore */ }
        try { this.tweens.killTweensOf(okBtn) } catch { /* ignore */ }
        try { bg.destroy() } catch { /* ignore */ }
        try { glow.destroy() } catch { /* ignore */ }
        try { title.destroy() } catch { /* ignore */ }
        try { panel.destroy() } catch { /* ignore */ }
        try { okBtn.destroy() } catch { /* ignore */ }
        try { dim.destroy() } catch { /* ignore */ }
      }
      const auto = this.time.delayedCall(4500, cleanup)
      const onTap = (): void => {
        try { auto.remove() } catch { /* ignore */ }
        cleanup()
      }
      okBtn.once('pointerup', onTap)
      dim.setInteractive({ useHandCursor: true })
      dim.once('pointerdown', onTap)
      this.input.once('pointerdown', onTap)
    })
  }

  // ──────────────────────────────────────────────────────
  // Sakura-themed UI + animations (per ADR-022/023)
  // 用 sakura 自己的 IMG-018/020/022/061-066/070-073/085-89/109-129 + 紫粉调色板
  // ──────────────────────────────────────────────────────

  private sakuraBarDrawn = false

  private drawSakuraTopBar(): void {
    const { CANVAS_W } = Layout
    const cfg = GameContext.config()
    const t = cfg.themeAssets!
    const pill = this.add.graphics().setDepth(20)
    pill.fillStyle(0x16051f, 0.72)
    pill.fillRoundedRect(CANVAS_W / 2 - 230, 38, 460, 82, 34)
    pill.lineStyle(2, 0xf9a8d4, 0.48)
    pill.strokeRoundedRect(CANVAS_W / 2 - 230, 38, 460, 82, 34)
    if (this.textures.exists('sakura_brand_brand_emblem')) {
      const emblem = this.add.image(CANVAS_W / 2 - 180, 79, 'sakura_brand_brand_emblem').setOrigin(0.5)
      fitTo(emblem, 52, 52)
      emblem.setDepth(21)
    }
    this.add.text(CANVAS_W / 2 + 26, 80, 'SAKURA NEON FORTUNE', {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: '30px',
      color: '#fff7c8',
      stroke: '#5b1a8e',
      strokeThickness: 5,
    }).setOrigin(0.5).setDepth(21)

    const buttonSpecs = [
      { x: 74, key: t.topIconBackKey, fallback: '<', onTap: () => this.goToGameLobby() },
      { x: CANVAS_W - 162, key: t.topIconInfoKey, fallback: 'i', onTap: () => this.showInfoPanel() },
      { x: CANVAS_W - 74, key: t.topIconSettingsKey, fallback: 'O', onTap: () => this.showSettingsPanel() },
    ] as const

    for (const spec of buttonSpecs) {
      if (spec.key && this.textures.exists(spec.key)) {
        const btn = this.add.image(spec.x, 82, spec.key).setOrigin(0.5).setDepth(21)
        fitTo(btn, 62, 62)
        btn.setAlpha(0.86)
        btn.setInteractive({ useHandCursor: true })
        btn.on('pointerdown', () => btn.setScale(btn.scale * 0.94))
        btn.on('pointerup', () => {
          fitTo(btn, 62, 62)
          spec.onTap()
        })
        btn.on('pointerout', () => fitTo(btn, 62, 62))
      } else {
        this.makeRoundIconButton(spec.x, 82, 62, spec.fallback, spec.onTap)
      }
    }
  }

  /**
   * Sakura 底部 control bar：IMG-022 一张图把 HUD（balance/totalbet/win 槽）+
   * 控制按钮区一起画完。drawHudThemed 和 drawControlBarThemed 共享这一张 bar。
   */
  private drawSakuraBar(): void {
    if (this.sakuraBarDrawn) return
    const cfg = GameContext.config()
    const t = cfg.themeAssets!
    const barKey = t.controlBarKey
    if (barKey && this.textures.exists(barKey)) {
      const bar = this.add.image(Layout.CANVAS_W / 2, 1480, barKey).setOrigin(0.5)
      const tex = bar.texture.getSourceImage()
      bar.setScale((Layout.CANVAS_W * 0.98) / tex.width).setDepth(-30)
      this.sakuraBarDrawn = true
      return
    }

    const { CANVAS_W } = Layout
    const barCY = 1680
    const barW = CANVAS_W * 0.92
    const barH = this.sakuraBarDispH()
    const x = (CANVAS_W - barW) / 2
    const y = barCY - barH / 2
    const g = this.add.graphics().setDepth(-30)
    g.fillStyle(0x16051f, 0.84)
    g.fillRoundedRect(x, y + 20, barW, barH - 20, 44)
    g.lineStyle(3, 0xf9a8d4, 0.45)
    g.strokeRoundedRect(x, y + 20, barW, barH - 20, 44)
    g.fillGradientStyle(0xec4899, 0xa855f7, 0x2d0a3a, 0x2d0a3a, 0.20, 0.20, 0.02, 0.02)
    g.fillRoundedRect(x + 18, y + 36, barW - 36, 86, 30)

    const labelStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: '21px',
      color: '#fce7f3',
      stroke: '#5b1a8e',
      strokeThickness: 4,
    }
    const labels = [
      { text: 'BALANCE', x: x + barW * 0.18 },
      { text: 'BET', x: x + barW * 0.36 },
      { text: 'WIN', x: x + barW * 0.82 },
    ]
    for (const label of labels) {
      this.add.text(label.x, y + 50, label.text, labelStyle).setOrigin(0.5).setDepth(-29)
    }
    this.sakuraBarDrawn = true
  }

  private sakuraBarDispH(): number {
    const cfg = GameContext.config()
    const key = cfg.themeAssets?.controlBarKey
    if (key && this.textures.exists(key)) {
      const tex = this.textures.get(key).getSourceImage()
      return tex.height * ((Layout.CANVAS_W * 0.98) / tex.width)
    }
    return 320
  }

  private drawHudThemed(): void {
    this.drawSakuraBar()
    const cfg = GameContext.config()
    const t = cfg.themeAssets!
    const txStroke = t.palette?.txtStroke ?? '#5b1a8e'
    const { CANVAS_W } = Layout
    const dispW = CANVAS_W * 0.98
    const dispH = this.sakuraBarDispH()
    const barTop = 1480 - dispH / 2

    // BALANCE value well in generated dock.
    const balCX = (CANVAS_W - dispW) / 2 + dispW * 0.222
    const balCY = barTop + dispH * 0.525
    this.balanceText = this.add.text(balCX, balCY, fmtBalance(this.balance, this.balanceHydrated), {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: '34px',
      color: '#fff7c8',
      stroke: txStroke,
      strokeThickness: 5,
      shadow: { offsetX: 0, offsetY: 2, color: '#000', blur: 4, fill: true },
    }).setOrigin(0.5)
    fitTextToWidth(this.balanceText, dispW * 0.26)

    // WIN value well.
    const winCX = (CANVAS_W - dispW) / 2 + dispW * 0.82
    const winCY = barTop + dispH * 0.525
    this.winText = this.add.text(winCX, winCY, '0', {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: '32px',
      color: '#fde047',
      stroke: txStroke,
      strokeThickness: 5,
      shadow: { offsetX: 0, offsetY: 2, color: '#000', blur: 4, fill: true },
    }).setOrigin(0.5)
    fitTextToWidth(this.winText, dispW * 0.17)
  }

  private drawControlBarThemed(): void {
    this.drawSakuraBar()
    const cfg = GameContext.config()
    const t = cfg.themeAssets!
    const txStroke = t.palette?.txtStroke ?? '#5b1a8e'
    const { CANVAS_W } = Layout
    const dispW = CANVAS_W * 0.98
    const dispH = this.sakuraBarDispH()
    const barTop = 1480 - dispH / 2

    // BET value well.
    const betCX = (CANVAS_W - dispW) / 2 + dispW * 0.416
    const betCY = barTop + dispH * 0.50
    this.betText = this.add.text(betCX, betCY, fmt(this.bet), {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: '32px',
      color: '#ffffff',
      stroke: txStroke,
      strokeThickness: 5,
      shadow: { offsetX: 0, offsetY: 2, color: '#000', blur: 4, fill: true },
    }).setOrigin(0.5)
    fitTextToWidth(this.betText, dispW * 0.10)

    // Bet- / Bet+ are baked into the dock; these are invisible hit zones.
    const btnY = barTop + dispH * 0.64
    const hit = 78
    this.add.zone((CANVAS_W - dispW) / 2 + dispW * 0.374, btnY, hit, hit)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => this.changeBet(-1))
    this.add.zone((CANVAS_W - dispW) / 2 + dispW * 0.466, btnY, hit, hit)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => this.changeBet(+1))

    // Overlay a real Spin sprite so idle/press/spin motion is visible over the baked dock.
    const spinCX = (CANVAS_W - dispW) / 2 + dispW * 0.606
    const spinCY = barTop + dispH * 0.49
    this.spinBtn = this.add.image(spinCX, spinCY, t.spinButtonKey ?? AssetKeys.ui.gen_spin_button).setOrigin(0.5)
    fitTo(this.spinBtn, 216, 216)
    this.spinBtn.setDepth(-20)
    this.spinBtnBaseScale = this.spinBtn.scale
    this.spinBtn.setInteractive({ useHandCursor: true })
    this.spinBtn.on('pointerdown', () => this.onSpinPressed())
    this.spinBtn.on('pointerup', () => this.onSpinReleased())
    this.startSpinIdleMotion()
  }

  private drawJackpotHeaderThemed(): void {
    const cfg = GameContext.config()
    const t = cfg.themeAssets!
    const cx = Layout.CANVAS_W / 2
    const headerKey = t.jackpotHeaderKey!

    // Sakura（walking_wilds 玩法，无 jackpot pool — 行业惯例：Jack and the Beanstalk
    // 类不配 4-tier jackpot ladder）：完全不渲染 jackpot header 图片，因为现成的
    // header art 把 GRAND/MAJOR/MINOR/MINI 标签烤进了 PNG，没法只去掉数值。把这块
    // 上方区域留给 walking-wild banner / FS counter 使用。
    if (cfg.id === 'sakura') {
      this.jackpotEntries = []
      this.drawSakuraFeatureBanner()
      return
    }
    void cx
    void headerKey

    // 其它有 jackpot pool 玩法的 4-tier ladder（保留供未来 Peacock 用）
    const cy = 185
    const displayW = Layout.CANVAS_W * 0.74
    const headerImg = this.add.image(cx, cy, headerKey).setOrigin(0.5)
    const tex = headerImg.texture.getSourceImage()
    const scale = displayW / tex.width
    headerImg.setScale(scale).setAlpha(0.88).setDepth(-20)

    const slots = [
      { rx: 0.205, value: 8_250_000, fontSize: 31, maxW: 570, color: '#fff7c8' },
      { rx: 0.468, value: 125_400,   fontSize: 29, maxW: 350, color: '#fce7f3' },
      { rx: 0.672, value: 5_820,     fontSize: 29, maxW: 350, color: '#dbeafe' },
      { rx: 0.872, value: 880,       fontSize: 28, maxW: 300, color: '#dcfce7' },
    ] as const
    const valueRy = 0.64
    const txStroke = t.palette?.txtStroke ?? '#5b1a8e'

    this.jackpotEntries = []
    for (const s of slots) {
      const x = cx + (s.rx - 0.5) * tex.width * scale
      const y = cy + (valueRy - 0.5) * tex.height * scale
      const txt = this.add.text(x, y, `$${fmt(s.value)}`, {
        fontFamily: 'Arial Black, Impact, sans-serif',
        fontSize: `${s.fontSize}px`,
        color: s.color,
        stroke: txStroke,
        strokeThickness: 4,
        shadow: { offsetX: 0, offsetY: 2, color: '#000', blur: 4, fill: true },
      }).setOrigin(0.5)
      const boxDispW = s.maxW * scale
      fitTextToWidth(txt, boxDispW)
      this.jackpotEntries.push({ text: txt, value: s.value, boxDispW, boxDispH: 0, prefix: '$' })
    }

    this.jackpotTickerEvent = this.time.addEvent({
      delay: 2600,
      loop: true,
      callback: () => this.tickJackpots(),
    })
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.jackpotTickerEvent?.remove(false)
      this.jackpotTickerEvent = null
    })
  }

  private drawSakuraFeatureBanner(): void {
    const { CANVAS_W } = Layout
    const x = 140
    const y = 300
    const w = CANVAS_W - 280
    const h = 118
    const g = this.add.graphics().setDepth(8)
    g.fillGradientStyle(0x2d0a3a, 0x2d0a3a, 0x17051f, 0x17051f, 0.76, 0.76, 0.66, 0.66)
    g.fillRoundedRect(x, y, w, h, 28)
    g.lineStyle(4, 0xfde047, 0.70)
    g.strokeRoundedRect(x, y, w, h, 28)
    g.lineStyle(2, 0xf9a8d4, 0.42)
    g.strokeRoundedRect(x + 11, y + 11, w - 22, h - 22, 20)

    this.add.text(CANVAS_W / 2, y + 42, 'WALKING WILDS', {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: '36px',
      color: '#fff7c8',
      stroke: '#5b1a8e',
      strokeThickness: 6,
      shadow: { offsetX: 0, offsetY: 3, color: '#000', blur: 5, fill: true },
    }).setOrigin(0.5).setDepth(9)

    this.add.text(CANVAS_W / 2, y + 82, 'Wilds step left  •  3 scatters = Free Spins', {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: '19px',
      color: '#fce7f3',
      stroke: '#2d0a3a',
      strokeThickness: 3,
      align: 'center',
    }).setOrigin(0.5).setDepth(9)
  }

  private showWinPopupThemed(
    level: 'big' | 'mega' | 'super' | 'jackpot',
    amount: number,
  ): Promise<void> {
    return new Promise((resolve) => {
      const cfg = GameContext.config()
      const t = cfg.themeAssets!
      const { CANVAS_W, CANVAS_H } = Layout
      const cx = CANVAS_W / 2
      const cy = CANVAS_H / 2

      const panelKey = (
        {
          big:     t.bigWinPanelKey,
          mega:    t.megaWinPanelKey ?? t.bigWinPanelKey,
          super:   t.superWinPanelKey ?? t.megaWinPanelKey ?? t.bigWinPanelKey,
          jackpot: t.jackpotWinPanelKey ?? t.superWinPanelKey ?? t.bigWinPanelKey,
        } as const
      )[level]!
      const palette = t.palette
      const txStroke = palette?.txtStroke ?? '#5b1a8e'

      const dim = this.add
        .rectangle(cx, cy, CANVAS_W, CANVAS_H, palette?.deep ?? 0x05000a, 0.76)
        .setOrigin(0.5)
        .setDepth(2000)

      const bgKey = t.bigWinBackdropKey
      let hdBg: Phaser.GameObjects.Image | null = null
      if (bgKey && this.textures.exists(bgKey)) {
        hdBg = this.add.image(cx, cy, bgKey).setOrigin(0.5).setDepth(2001)
        const bgTex = hdBg.texture.getSourceImage()
        const bgScale = Math.max(CANVAS_W / bgTex.width, CANVAS_H / bgTex.height)
        hdBg.setScale(bgScale).setAlpha(0)
        this.tweens.add({ targets: hdBg, alpha: 0.44, duration: 360, ease: 'Sine.easeOut' })
      }

      const burstKey = t.celebrationBurstKey
      let burst: Phaser.GameObjects.Image | null = null
      if (burstKey && this.textures.exists(burstKey)) {
        burst = this.add.image(cx, cy - 10, burstKey).setOrigin(0.5).setDepth(2002)
        fitTo(burst, CANVAS_W * 0.88, CANVAS_W * 0.88)
        burst.setBlendMode(Phaser.BlendModes.ADD).setAlpha(0)
        this.tweens.add({ targets: burst, alpha: 0.38, duration: 360 })
        this.tweens.add({
          targets: burst,
          rotation: Math.PI * 2,
          duration: 18000,
          repeat: -1,
          ease: 'Linear',
        })
        this.tweens.add({
          targets: burst,
          scale: burst.scale * 1.08,
          yoyo: true,
          repeat: -1,
          duration: 1100,
          ease: 'Sine.inOut',
        })
      }

      const titleByLevel = {
        big: 'BIG WIN',
        mega: 'MEGA WIN',
        super: 'SUPER WIN',
        jackpot: 'JACKPOT',
      } as const
      const levelColor = {
        big: '#fff7c8',
        mega: '#fde68a',
        super: '#ffe4f3',
        jackpot: '#fff1a8',
      } as const
      const titleSize = {
        big: 86,
        mega: 82,
        super: 78,
        jackpot: 76,
      } as const

      const panel = this.add.image(cx, cy - 6, panelKey).setOrigin(0.5).setDepth(2003)
      const panelTex = panel.texture.getSourceImage()
      const panelScale = (CANVAS_W * 1.08) / panelTex.width
      panel.setScale(panelScale * 0.5).setAlpha(0)
      this.tweens.add({
        targets: panel,
        scale: panelScale,
        alpha: 1,
        duration: 480,
        delay: 120,
        ease: 'Back.easeOut',
      })

      const titleTxt = this.add.text(cx, cy - 250, titleByLevel[level], {
        fontFamily: 'Arial Black, Impact, sans-serif',
        fontSize: `${titleSize[level]}px`,
        color: levelColor[level],
        stroke: '#5b110f',
        strokeThickness: 11,
        align: 'center',
        shadow: { offsetX: 0, offsetY: 6, color: '#000', blur: 10, fill: true },
      }).setOrigin(0.5).setDepth(2004)
      titleTxt.setAlpha(0)
      fitTextToWidth(titleTxt, CANVAS_W * 0.74)
      this.tweens.add({ targets: titleTxt, alpha: 1, y: cy - 270, duration: 420, delay: 300, ease: 'Cubic.easeOut' })

      const amountStyle: Phaser.Types.GameObjects.Text.TextStyle = {
        fontFamily: 'Arial Black, Impact, sans-serif',
        fontSize: level === 'jackpot' ? '108px' : level === 'super' ? '100px' : '92px',
        color: '#fff7c8',
        stroke: txStroke,
        strokeThickness: 9,
        align: 'center',
        shadow: { offsetX: 0, offsetY: 6, color: '#000', blur: 8, fill: true },
      }
      const amountTxt = this.add
        .text(cx, cy + 72, '0', amountStyle)
        .setOrigin(0.5)
        .setDepth(2004)
      amountTxt.setAlpha(0)
      this.tweens.add({ targets: amountTxt, alpha: 1, duration: 320, delay: 480 })

      const coinsTxt = this.add.text(cx, cy + 164, 'COINS', {
        fontFamily: 'Arial Black, Impact, sans-serif',
        fontSize: '30px',
        color: '#ffd166',
        stroke: '#4a1028',
        strokeThickness: 5,
        align: 'center',
      }).setOrigin(0.5).setDepth(2004).setAlpha(0)
      this.tweens.add({ targets: coinsTxt, alpha: 0.9, duration: 320, delay: 620 })
      let popupAlive = true

      const countDur =
        level === 'jackpot' ? 2400 : level === 'super' ? 1800 : level === 'mega' ? 1400 : 1100
      this.time.delayedCall(560, () => {
        const counter = { v: 0 }
        this.tweens.add({
          targets: counter,
          v: amount,
          duration: countDur,
          ease: 'Cubic.easeOut',
          onUpdate: () => {
            if (!popupAlive || !amountTxt.active) return
            amountTxt.setText(fmt(Math.floor(counter.v)))
            fitTextToWidth(amountTxt, CANVAS_W * 0.76)
          },
          onComplete: () => {
            if (!popupAlive || !amountTxt.active) return
            amountTxt.setText(fmt(amount))
            fitTextToWidth(amountTxt, CANVAS_W * 0.76)
            this.tweens.add({
              targets: amountTxt,
              scale: 1.08,
              yoyo: true,
              duration: 220,
              ease: 'Cubic.easeOut',
            })
          },
        })
        this.playSfx(this.sfx('big_win_count'), 0.7)
      })

      // 金币雨（sakura 主题 = IMG-109 coin_burst 粒子）
      const coinKey = t.coinParticleKey
      let coinRain: Phaser.GameObjects.Particles.ParticleEmitter | null = null
      if (coinKey && this.textures.exists(coinKey)) {
        coinRain = this.add.particles(cx, -40, coinKey, {
          x: { min: 60, max: CANVAS_W - 60 },
          y: -40,
          speedY: { min: 240, max: 430 },
          speedX: { min: -42, max: 42 },
          scale: { start: 0.13, end: 0.05 },
          alpha: { start: 0.82, end: 0.22 },
          lifespan: 2200,
          frequency: level === 'jackpot' ? 120 : 170,
          quantity: level === 'jackpot' ? 2 : 1,
          rotate: { min: 0, max: 360 },
          gravityY: 170,
        })
        coinRain.setDepth(2002)
      }

      let cleaned = false
      const cleanup = (): void => {
        if (cleaned) return
        cleaned = true
        popupAlive = false
        try { resolve() } catch { /* ignore */ }
        try { coinRain?.stop(); coinRain?.destroy() } catch { /* ignore */ }
        try { if (hdBg)  { this.tweens.killTweensOf(hdBg);  hdBg.destroy() } } catch { /* ignore */ }
        try { if (burst) { this.tweens.killTweensOf(burst); burst.destroy() } } catch { /* ignore */ }
        try { this.tweens.killTweensOf(panel) } catch { /* ignore */ }
        try { this.tweens.killTweensOf(titleTxt) } catch { /* ignore */ }
        try { this.tweens.killTweensOf(amountTxt) } catch { /* ignore */ }
        try { this.tweens.killTweensOf(coinsTxt) } catch { /* ignore */ }
        try { panel.destroy() } catch { /* ignore */ }
        try { titleTxt.destroy() } catch { /* ignore */ }
        try { amountTxt.destroy() } catch { /* ignore */ }
        try { coinsTxt.destroy() } catch { /* ignore */ }
        try { dim.destroy() } catch { /* ignore */ }
      }

      const holdMs =
        level === 'jackpot' ? 4000 :
        level === 'super'   ? 3400 :
        level === 'mega'    ? 3000 :
                              2600
      const auto = this.time.delayedCall(holdMs, cleanup)
      dim.setInteractive({ useHandCursor: true })
      const onTap = (): void => {
        try { auto.remove() } catch { /* ignore */ }
        cleanup()
      }
      dim.once('pointerdown', onTap)
      this.input.once('pointerdown', onTap)
    })
  }

  private showFreeSpinsEventThemed(spins: number): Promise<void> {
    return new Promise((resolve) => {
      const cfg = GameContext.config()
      const t = cfg.themeAssets!
      const { CANVAS_W, CANVAS_H } = Layout
      const cx = CANVAS_W / 2
      const cy = CANVAS_H / 2
      const palette = t.palette
      const txStroke = palette?.txtStroke ?? '#5b1a8e'

      const dim = this.add
        .rectangle(cx, cy, CANVAS_W, CANVAS_H, palette?.deep ?? 0x05000a, 0.92)
        .setOrigin(0.5)
        .setDepth(2000)

      // Sakura v2 uses one full-screen art asset with the fixed "FREE SPINS" label baked in.
      const screenKey = t.fsStartScreenKey
      let bg: Phaser.GameObjects.Image | null = null
      if (screenKey && this.textures.exists(screenKey)) {
        bg = this.add.image(cx, cy, screenKey).setOrigin(0.5).setDepth(2001)
        const bgTex = bg.texture.getSourceImage()
        const bgScale = Math.max(CANVAS_W / bgTex.width, CANVAS_H / bgTex.height)
        bg.setScale(bgScale).setAlpha(0)
        this.tweens.add({ targets: bg, alpha: 1, duration: 420 })
      } else {
        const bgKey = t.fsBackdropKey
        if (bgKey && this.textures.exists(bgKey)) {
          bg = this.add.image(cx, cy, bgKey).setOrigin(0.5).setDepth(2001)
          const bgTex = bg.texture.getSourceImage()
          const bgScale = Math.max(CANVAS_W / bgTex.width, CANVAS_H / bgTex.height)
          bg.setScale(bgScale).setAlpha(0)
          this.tweens.add({ targets: bg, alpha: 0.92, duration: 420 })
        }
      }

      let panel: Phaser.GameObjects.Image | null = null
      if (!screenKey || !this.textures.exists(screenKey)) {
        const panelKey = t.fsStartPanelKey!
        panel = this.add.image(cx, cy - 20, panelKey).setOrigin(0.5).setDepth(2003)
        const panelTex = panel.texture.getSourceImage()
        const panelScale = (CANVAS_W * 0.72) / panelTex.width
        panel.setScale(panelScale * 0.5).setAlpha(0)
        this.tweens.add({
          targets: panel,
          scale: panelScale,
          alpha: 1,
          duration: 540,
          delay: 180,
          ease: 'Back.easeOut',
        })
      }

      // 数字 N（动态内容，固定标题已由素材承载）
      const numY = screenKey && this.textures.exists(screenKey) ? cy + 245 : cy + 60
      const numBack = this.add.graphics().setDepth(2004)
      if (screenKey && this.textures.exists(screenKey)) {
        numBack.fillStyle(0x2d0a3a, 0.32)
        numBack.fillRoundedRect(cx - 138, numY - 82, 276, 164, 36)
        numBack.lineStyle(3, 0xfff7c8, 0.45)
        numBack.strokeRoundedRect(cx - 138, numY - 82, 276, 164, 36)
      }
      const numTxt = this.add
        .text(cx, numY, `${spins}`, {
          fontFamily: 'Arial Black, Impact, sans-serif',
          fontSize: screenKey && this.textures.exists(screenKey) ? '128px' : '150px',
          color: '#fff7c8',
          stroke: txStroke,
          strokeThickness: 12,
          shadow: { offsetX: 0, offsetY: 6, color: '#000', blur: 8, fill: true },
        })
        .setOrigin(0.5)
        .setDepth(2005)
      numTxt.setAlpha(screenKey && this.textures.exists(screenKey) ? 1 : 0).setScale(screenKey && this.textures.exists(screenKey) ? 1 : 0.4)
      this.tweens.add({
        targets: numTxt,
        alpha: 1,
        scale: 1,
        duration: 540,
        delay: 460,
        ease: 'Back.easeOut',
      })

      // Fallback only: old panel needs a small descriptor because it has no number context.
      const labelTxt = this.add
        .text(cx, cy + 220, screenKey && this.textures.exists(screenKey) ? '' : 'FREE SPINS AWARDED', {
          fontFamily: 'Arial Black, Impact, sans-serif',
          fontSize: '34px',
          color: '#ffffff',
          stroke: txStroke,
          strokeThickness: 6,
        })
        .setOrigin(0.5)
        .setDepth(2004)
      labelTxt.setAlpha(0)
      this.tweens.add({
        targets: labelTxt,
        alpha: 1,
        duration: 360,
        delay: 580,
      })

      // 樱花飘落粒子（sakura 主题 = IMG-128 sakura_petal_sweep 或 IMG-111 sakura_burst）
      const petalKey = t.driftParticleKey
      let drops: Phaser.GameObjects.Particles.ParticleEmitter | null = null
      if (petalKey && this.textures.exists(petalKey)) {
        drops = this.add.particles(cx, -60, petalKey, {
          x: { min: 0, max: CANVAS_W },
          y: -60,
          speedY: { min: 140, max: 260 },
          speedX: { min: -40, max: 40 },
          scale: { start: 0.18, end: 0.10 },
          alpha: { start: 0.95, end: 0.4 },
          lifespan: 4200,
          frequency: 200,
          quantity: 1,
          rotate: { min: 0, max: 360 },
        })
        drops.setDepth(2002)
      }

      // SFX
      this.playSfx(this.sfx('free_spins_trigger'), 0.65)
      this.time.delayedCall(640, () =>
        this.playSfx(this.sfx('free_spin_start'), 0.55),
      )

      let cleaned = false
      const cleanup = (): void => {
        if (cleaned) return
        cleaned = true
        try { resolve() } catch { /* ignore */ }
        try { drops?.stop(); drops?.destroy() } catch { /* ignore */ }
        try { if (bg) { this.tweens.killTweensOf(bg); bg.destroy() } } catch { /* ignore */ }
        try { if (panel) this.tweens.killTweensOf(panel) } catch { /* ignore */ }
        try { this.tweens.killTweensOf(numBack) } catch { /* ignore */ }
        try { this.tweens.killTweensOf(numTxt) } catch { /* ignore */ }
        try { this.tweens.killTweensOf(labelTxt) } catch { /* ignore */ }
        try { panel?.destroy() } catch { /* ignore */ }
        try { numBack.destroy() } catch { /* ignore */ }
        try { numTxt.destroy() } catch { /* ignore */ }
        try { labelTxt.destroy() } catch { /* ignore */ }
        try { dim.destroy() } catch { /* ignore */ }
      }
      const auto = this.time.delayedCall(4500, cleanup)
      dim.setInteractive({ useHandCursor: true })
      const onTap = (): void => {
        try { auto.remove() } catch { /* ignore */ }
        cleanup()
      }
      dim.once('pointerdown', onTap)
      this.input.once('pointerdown', onTap)
    })
  }
}

// ─── helpers ───────────────────────────────────────────
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function isMobileThermalDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  return navigator.maxTouchPoints > 1 && /iPhone|iPad|iPod|Android/i.test(ua)
}

/** 生成纯随机的 reel grid（spectator 视觉 replay 用，不参与结算） */
function generateRandomGrid(): SymbolKey[][] {
  const pool: SymbolKey[] = [
    'CHERRY', 'LEMON', 'WATERMELON', 'STRAWBERRY', 'BLUEBERRY',
    'RAINBOW', 'CROWN', 'GOLDEN7',
  ]
  const cols: SymbolKey[][] = []
  for (let c = 0; c < Grid.COLS; c++) {
    const col: SymbolKey[] = []
    for (let r = 0; r < Grid.ROWS; r++) {
      col.push(pool[Math.floor(Math.random() * pool.length)]!)
    }
    cols.push(col)
  }
  return cols
}

/** 生成「带 cluster 的」grid —— 随机塞 6-10 个同 symbol 在邻近格里，让 spectator 看到中奖盘面 */
function generateWinningGrid(): {
  grid: SymbolKey[][]
  clusterCells: [number, number][]
  symbol: SymbolKey
} {
  const grid = generateRandomGrid()
  const pool: SymbolKey[] = ['CHERRY', 'LEMON', 'WATERMELON', 'STRAWBERRY', 'BLUEBERRY', 'RAINBOW']
  const sym = pool[Math.floor(Math.random() * pool.length)]!
  const startC = Math.floor(Math.random() * (Grid.COLS - 2))
  const startR = Math.floor(Math.random() * (Grid.ROWS - 2))
  const clusterSize = 5 + Math.floor(Math.random() * 4)
  const seeded = new Set<string>()
  const clusterCells: [number, number][] = []
  const queue: Array<[number, number]> = [[startC, startR]]
  while (clusterCells.length < clusterSize && queue.length > 0) {
    const [c, r] = queue.shift()!
    const key = `${c},${r}`
    if (seeded.has(key)) continue
    if (c < 0 || c >= Grid.COLS || r < 0 || r >= Grid.ROWS) continue
    seeded.add(key)
    grid[c]![r] = sym
    clusterCells.push([c, r])
    // 邻接 4 方向加入候选
    queue.push([c + 1, r], [c - 1, r], [c, r + 1], [c, r - 1])
  }
  return { grid, clusterCells, symbol: sym }
}

function fitTo(img: Phaser.GameObjects.Image, w: number, h: number): void {
  const tex = img.texture.getSourceImage()
  const scale = Math.min(w / tex.width, h / tex.height)
  img.setScale(scale)
}

function fmt(n: number): string {
  return n.toLocaleString('en-US')
}

/** Format balance for HUD: hydration-aware. Show "—" until real /me lands so we
 *  never lie with placeholder 88,888,888 (the old default before hydration). */
function fmtBalance(n: number, hydrated: boolean): string {
  if (!hydrated) return '—'
  return n.toLocaleString('en-US')
}

/** 把 Text 缩到不超过 maxWidth；超出则按比例 setScale 缩小。 */
function fitTextToWidth(text: Phaser.GameObjects.Text, maxWidth: number): void {
  text.setScale(1)
  const w = text.width
  if (w > maxWidth) text.setScale(maxWidth / w)
}

void ApiConfig
